import React, { useState, useRef, useEffect , useCallback} from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { getCommunityPostUrl, getForumPostUrl, getLegacyPostUrl } from '../../utils/postUrls';
import {
  Heart,
  MessageSquare,
  Share2,
  Bookmark,
  Send,
  Search,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Flame,
  Clock,
  Calendar,
  Users,
  X,
  Hash,
  AlertCircle,
  Compass,
  Repeat2,
  Link2,
  ThumbsDown,
  Upload,
  MoreVertical,
  Edit3,
  Pin,
  Trash,
  Bell,
  RefreshCw,
  LogOut,
} from "lucide-react";
import { PixelatedTurtle } from '@theme/design-constants';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigation } from '../../context/NavigationContext';
import { ThemeCustomizerButton, SiteThemeCustomizer } from "./ThemeCustomizer/index";
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import { DEFAULT_THEME } from '../../shared/theme/tokens';
import AnimationLayer from "./AnimationLayer";
import { getThemeAnimations } from "@theme/animation-config";
import { useScale, scaleW, scaleH } from '../../hooks/useScale';
import { useDynamicBackground } from '../../hooks/useDynamicBackground';
import { useSwipe } from '../../hooks/useSwipe';
import { Post } from '../../types/post';
import { canModeratePost, getEditorType, getPinnerType, User as PermUser } from '../../utils/permissions';
import { EditPostModal } from './EditPostModal';
import { PostBadges } from './PostBadges';
import LoadingScreen from './LoadingScreen';
import { MediaUploadButton } from './MediaUploadButton';
import { MediaDisplay } from './MediaDisplay';
import { CommunityPostModal } from './PostCreation';
import MainNavbar from './MainNavbar';
import { commentEventBus } from '../../lib/commentEventBus';
import { useCommentSync } from '../../hooks/useCommentSync';
import { postEventBus } from '../../lib/postEventBus';
import { usePostSync } from '../../hooks/usePostSync';
import { notificationEventBus } from '../../lib/notificationEventBus';
import { useNotificationSync } from '../../hooks/useNotificationSync';
import SoftLoad from './SoftLoad';
import { getBackground, getCardBackground, calculateNavbarBg } from '../../utils/backgroundHelpers';
import resolveAvatar from '../../lib/resolveAvatar';
import { SubPanelSwiper } from '../mobile/Navigation';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { normalizeLikeCounts } from '../shared/hooks/useFeedData';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
type User = {
  id: string;
  username: string;
  displayName?: string;
  avatar?: string;
  isSiteAdmin?: boolean;
  isSuperAdmin?: boolean;
  isSiteModerator?: boolean;
};

type Community = {
  id: string;
  name: string;
  nameSlug?: string;
  description?: string;
  avatar?: string;
  memberCount: number;
  isMember: boolean;
};

type Reply = {
  id: string;
  content: string;
  author: {
    id: number;
    username: string;
    profileName?: string;
    profilePicture?: string;
  };
  likes: number;
  dislikes: number;
  liked?: boolean;
  disliked?: boolean;
  createdAt: string;
};

type CommunityPost = {
  id: string;
  author: string;
  authorId: string;
  authorAvatar?: string;
  authorUsername?: string;
  communityId: string;
  communityName: string;
  communitySlug?: string;
  title?: string;
  slug?: string;
  postType?: 'community' | 'forum';
  content: string;
  tags?: string[];
  mediaUrl?: string;
  imageUrl?: string;
  aboveContent?: boolean;
  belowContent?: boolean;
  likes: number;
  dislikes?: number;
  disliked?: boolean;
  comments: number;
  replies?: Reply[]; // Preview replies
  shares: number;
  timestamp: string;
  liked: boolean;
  createdAt: string;
  editedByModeratorId?: number;
  editedByModeratorAt?: string;
  moderatorEditReason?: string;
  isPinned?: boolean;
  pinnedBy?: string;
  pinnedByType?: 'admin' | 'mod' | 'community_admin' | 'community_mod';
  pinnedAt?: string;
  editedBy?: string;
  editedByType?: 'user' | 'admin' | 'mod' | 'community_admin' | 'community_mod';
  editedAt?: string;
};

type Props = {
  currentUser: User | null;
  initialPosts: CommunityPost[];
  communities: Community[];
  isAuthenticated?: boolean;
  mobileMode?: boolean;
  mobileSection?: number;
  onSectionChange?: (index: number) => void;
  mobileScreenWidth?: number;
};

// Note: PixelatedTurtle is imported from @theme/design-constants at the top
// It already accepts a color prop

const PixelatedCoral = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" className="pixelated">
    <g fill="#f97316">
      <rect x="14" y="8" width="4" height="4" />
      <rect x="12" y="12" width="8" height="4" />
      <rect x="10" y="16" width="4" height="4" />
      <rect x="18" y="16" width="4" height="4" />
      <rect x="14" y="20" width="4" height="4" />
    </g>
  </svg>
);

/**
 * Select 3 preview comments based on:
 * 1. Most popular (highest likes)
 * 2. Most controversial (highest dislikes OR closest like/dislike ratio)
 * 3. Random
 */
function selectPreviewComments(replies: Reply[]): Reply[] {
  if (!replies || replies.length === 0) return [];
  if (replies.length <= 3) return replies;

  const selected: Reply[] = [];
  const available = [...replies];

  // 1. Most popular (highest likes)
  const mostPopular = available.reduce((prev, curr) =>
    (curr.likes > prev.likes) ? curr : prev
  );
  selected.push(mostPopular);
  available.splice(available.indexOf(mostPopular), 1);

  // 2. Most controversial (highest dislikes, or if no dislikes, smallest like-dislike difference)
  const mostControversial = available.reduce((prev, curr) => {
    const prevScore = prev.dislikes > 0 ? prev.dislikes : Math.abs(prev.likes - prev.dislikes);
    const currScore = curr.dislikes > 0 ? curr.dislikes : Math.abs(curr.likes - curr.dislikes);
    return currScore > prevScore ? curr : prev
  });
  selected.push(mostControversial);
  available.splice(available.indexOf(mostControversial), 1);

  // 3. Random
  if (available.length > 0) {
    const randomIndex = Math.floor(Math.random() * available.length);
    selected.push(available[randomIndex]);
  }

  return selected;
}

export default function CommunityList({ currentUser, initialPosts, communities, isAuthenticated = true, mobileMode = false, mobileSection, onSectionChange, mobileScreenWidth = 390 }: Props) {
  const navigate = useNavigate();
  const { theme, currentPreset, siteCustomization, siteAnimations, ensureSiteThemeApplied } = useTheme();
  const { logout, setShowLoginModal } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const rawScale = useScale();
  const scale = mobileMode ? { w: 1, h: 1 } : rawScale;
  const backgroundHeight = useDynamicBackground();
  const [posts, setPosts] = useState<CommunityPost[]>(initialPosts);
  const [isPosting, setIsPosting] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [friendsPosts, setFriendsPosts] = useState<any[]>([]);
  const [followingPosts, setFollowingPosts] = useState<any[]>([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortBy, setSortBy] = useState<string>("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"profiles" | "communities">("communities");
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState("");
  const [sidebarSearchType, setSidebarSearchType] = useState<"people" | "communities" | "tags">("communities");
  const [showSidebarSearchResults, setShowSidebarSearchResults] = useState(false);
  const [sidebarSearchPeople, setSidebarSearchPeople] = useState<any[]>([]);
  const [sidebarSearchCommunities, setSidebarSearchCommunities] = useState<any[]>([]);
  const [sidebarSearchTags, setSidebarSearchTags] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [followedTags, setFollowedTags] = useState<string[]>([]);
  const [showSiteCustomizer, setShowSiteCustomizer] = useState(false);
  const [isShareAnimating, setIsShareAnimating] = useState(false);
  const [showPostWidget, setShowPostWidget] = useState(false);
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // UNIFIED CACHE: Use animations from ThemeContext
    if (siteAnimations) {
      return siteAnimations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [customization, setCustomization] = useState<any>(siteCustomization || {
    textColor: theme.textColor,
    accentColor: theme.accentColor,
    primaryColor: theme.primaryColor,
    secondaryColor: theme.secondaryColor,
    cardBg: theme.cardBg,
    navbarBg: theme.navbarBg,
    tabBarBg: theme.tabBarBg,
    colorSpace: 'Off',
    gradientEnabled: true,
    gradientAngle: 135,
    gradientFavorability: 50,
    gradientStrength: 100,
    temperatureLimit: 50,
    magentaLimit: 50,
    intensityLimit: 50,
    backgroundSaturationLimit: 50,
    backgroundBrightnessLimit: 50,
    redLimit: 50,
    greenLimit: 50,
    blueLimit: 50,
    backgroundContrast: 50,
    backgroundHueGravity: 0,
    backgroundGraySaturation: 0,
    backgroundDefaultHue: 184,
  });

  const [showCreateCommunityModal, setShowCreateCommunityModal] = useState(false);
  const [newCommunityName, setNewCommunityName] = useState("");
  const [newCommunityTitle, setNewCommunityTitle] = useState("");
  const [newCommunityBio, setNewCommunityBio] = useState("");
  const [newCommunityIsPublic, setNewCommunityIsPublic] = useState(true);
  const [isCreatingCommunity, setIsCreatingCommunity] = useState(false);
  const [showModerateMenu, setShowModerateMenu] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Focus animation state
  const [focusedPostId, setFocusedPostId] = useState<string | null>(null);
  const [focusedPostIndex, setFocusedPostIndex] = useState<number>(-1);
  const postRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const currentFocusIndexRef = useRef<number>(-1);
  const isThrottledRef = useRef<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const effectiveIsMobile = mobileMode || isMobile;

  // Pull-to-refresh for mobile center column
  const pullToRefreshCallback = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await fetchWithAuth('/api/communities/posts', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPosts((data.posts || []).map(normalizeLikeCounts));
      }
    } catch { /* silent */ }
    setTimeout(() => setIsRefreshing(false), 300);
  }, []);
  const pullToRefresh = usePullToRefresh({ onRefresh: pullToRefreshCallback });

  const [focusAnimationEnabled, setFocusAnimationEnabled] = useState(false);

  // Repost/Share modal state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostPostId, setRepostPostId] = useState<string | null>(null);
  const [repostComment, setRepostComment] = useState('');
  const [isReposting, setIsReposting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState<string | null>(null);

  // Draggable modal state
  const [repostModalPosition, setRepostModalPosition] = useState({ x: 0, y: 0 });
  const [isDraggingRepost, setIsDraggingRepost] = useState(false);
  const [dragStartRepost, setDragStartRepost] = useState({ x: 0, y: 0 });
  const repostModalRef = useRef<HTMLDivElement>(null);

  // Section navigation state
  const [currentSection, setCurrentSection] = useState<'left' | 'middle' | 'right'>('middle');

  // Create local theme with transformed colors for inline styles
  // Simply use theme from ThemeContext - it already has computedColors (transformed)
  // CRITICAL: Don't re-transform! Theme from ThemeContext is already correct
  const localTheme = React.useMemo(() => {
    // Theme from ThemeContext always has all required properties defined
    // from base presets - just return it directly
    return theme;
  }, [theme]);

  // Helper to ensure required theme properties for functions expecting ThemeColors
  const ensureThemeColors = React.useMemo(() => ({
    primaryColor: localTheme.primaryColor || DEFAULT_THEME.primaryColor!,
    secondaryColor: localTheme.secondaryColor || DEFAULT_THEME.secondaryColor!,
    cardBg: localTheme.cardBg || DEFAULT_THEME.cardBg!,
    cardGradientColor1: localTheme.cardGradientColor1,
    cardGradientColor2: localTheme.cardGradientColor2,
  }), [localTheme]);

  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, opacity: number): string => {
    if (hex.startsWith('rgba')) {
      const match = hex.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
      }
      return hex;
    }

    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Helper to get dropdown button background with custom opacity
  const getDropdownButtonBg = () => {
    // Use dropdownColor (hex), or fall back to primaryColor (hex) - not tabBarBg which is already rgba
    const baseColor = localTheme.dropdownColor || localTheme.primaryColor || DEFAULT_THEME.primaryColor!;
    const opacity = (customization.dropdownButtonOpacity ?? 100) / 100;
    return hexToRgba(baseColor, opacity);
  };

  // Helper to get dropdown menu background with custom opacity
  const getDropdownMenuBg = () => {
    // Use dropdownColor (hex), or fall back to primaryColor (hex) - not tabBarBg which is already rgba
    const baseColor = localTheme.dropdownColor || localTheme.primaryColor || DEFAULT_THEME.primaryColor!;
    const opacity = (customization.dropdownOpacity ?? 95) / 100;
    return hexToRgba(baseColor, opacity);
  };

  // Use localTheme directly instead of recalculating
  const themeColors = {
    navbarBg: localTheme.navbarBg,
    accentColor: localTheme.accentColor,
    textColor: localTheme.textColor,
    tabBarBg: localTheme.tabBarBg,
    dropdownButtonBg: getDropdownButtonBg(),
    dropdownMenuBg: getDropdownMenuBg(),
    gradientFrom: localTheme.primaryColor || customization.gradientFrom,
    gradientTo: localTheme.secondaryColor || customization.gradientTo
  };

  // Apply customization as CSS variables when localTheme changes
  React.useEffect(() => {
    const root = document.documentElement;

    // Helper to blend two colors
    const blendColors = (color1: string, color2: string, ratio: number) => {
      const r1 = parseInt(color1.slice(1, 3), 16);
      const g1 = parseInt(color1.slice(3, 5), 16);
      const b1 = parseInt(color1.slice(5, 7), 16);

      const r2 = parseInt(color2.slice(1, 3), 16);
      const g2 = parseInt(color2.slice(3, 5), 16);
      const b2 = parseInt(color2.slice(5, 7), 16);

      const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
      const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
      const b = Math.round(b1 * (1 - ratio) + b2 * ratio);

      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Apply background gradient
    if (customization.gradientEnabled) {
      const ratio = (customization.gradientFavorability || 50) / 100;
      const blendedColor1 = blendColors(localTheme.primaryColor || DEFAULT_THEME.primaryColor!, localTheme.secondaryColor || DEFAULT_THEME.secondaryColor!, ratio);
      const blendedColor2 = blendColors(localTheme.secondaryColor || DEFAULT_THEME.secondaryColor!, localTheme.primaryColor || DEFAULT_THEME.primaryColor!, ratio);
      const strength = (customization.gradientStrength || 100) / 100;
      const finalColor1 = blendColors('#808080', blendedColor1, strength);
      const finalColor2 = blendColors('#808080', blendedColor2, strength);

      root.style.setProperty('--app-bg', `linear-gradient(${customization.gradientAngle || 135}deg, ${finalColor1}, ${finalColor2})`);
    } else {
      root.style.setProperty('--app-bg', localTheme.primaryColor || DEFAULT_THEME.primaryColor!);
    }

    // Apply other CSS variables (using transformed colors from localTheme)
    root.style.setProperty('--color-text', localTheme.textColor || DEFAULT_THEME.textColor!);
    root.style.setProperty('--color-accent', localTheme.accentColor || DEFAULT_THEME.accentColor!);
    root.style.setProperty('--navbar-bg', localTheme.navbarBg || DEFAULT_THEME.navbarBg!);
    root.style.setProperty('--tab-bar-bg', localTheme.tabBarBg || DEFAULT_THEME.tabBarBg!);

    // Apply card background with gradient if enabled
    if (customization.cardGradientEnabled) {
      const ratio = (customization.cardGradientFavorability || 50) / 100;
      const blendedCard1 = blendColors(customization.cardGradientColor1, customization.cardGradientColor2, ratio);
      const blendedCard2 = blendColors(customization.cardGradientColor2, customization.cardGradientColor1, ratio);
      const strength = (customization.cardGradientStrength || 100) / 100;
      const finalCard1 = blendColors(localTheme.cardBg, blendedCard1, strength);
      const finalCard2 = blendColors(localTheme.cardBg, blendedCard2, strength);

      const gradientBg = `linear-gradient(${customization.cardGradientAngle || 120}deg, ${hexToRgba(finalCard1, customization.cardOpacity || 100)}, ${hexToRgba(finalCard2, customization.cardOpacity || 100)})`;
      root.style.setProperty('--card-bg', gradientBg);
    } else {
      root.style.setProperty('--card-bg', hexToRgba(localTheme.cardBg, customization.cardOpacity || 100));
    }
  }, [localTheme, customization]);

  // Listen for post updates from other pages/components
  usePostSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === data.postId
          ? { ...p, liked: data.liked, likes: data.likes }
          : p
      ));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === data.postId
          ? { ...p, disliked: data.disliked, dislikes: data.dislikes }
          : p
      ));
    },
    postIds: posts.map(p => p.id)
  });

  // Listen for comment updates from other pages/components
  useCommentSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === data.commentId.toString()
                ? { ...r, liked: data.liked, likes: data.likes }
                : r
            ),
          };
        }
        return p;
      }));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === data.commentId.toString()
                ? { ...r, disliked: data.disliked, dislikes: data.dislikes }
                : r
            ),
          };
        }
        return p;
      }));
    },
  });

  // Notification synchronization for real-time updates
  useNotificationSync({
    onNew: (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onRead: (notificationId) => {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    },
    onReadAll: () => {
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    },
    onDelete: (notificationId) => {
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    },
  });

  // Swipe handlers
  const swipeRef = useSwipe({
    onSwipeLeft: () => {
      if (currentSection === 'middle') setCurrentSection('right');
      else if (currentSection === 'left') setCurrentSection('middle');
    },
    onSwipeRight: () => {
      if (currentSection === 'middle') setCurrentSection('left');
      else if (currentSection === 'right') setCurrentSection('middle');
    }
  });

  // Sync section changes to parent (mobile shell)
  useEffect(() => {
    if (mobileMode && onSectionChange) {
      const sectionIndex = { left: 0, middle: 1, right: 2 }[currentSection];
      onSectionChange(sectionIndex);
    }
  }, [currentSection, mobileMode, onSectionChange]);

  // Sync from parent (mobile shell) to local section
  useEffect(() => {
    if (mobileMode && mobileSection !== undefined) {
      const sections = ['left', 'middle', 'right'] as const;
      if (sections[mobileSection] !== currentSection) {
        setCurrentSection(sections[mobileSection]);
      }
    }
  }, [mobileMode, mobileSection]);

  // Site theme is already loaded by ThemeContext

  // Sync animations when siteAnimations or currentPreset changes
  useEffect(() => {
    if (siteAnimations) {
      setThemeAnimations(siteAnimations);
    } else {
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [siteAnimations, currentPreset]);

  // Register site theme with NavigationContext for LoadingScreen continuity
  useEffect(() => {
    if (!siteCustomization) {
      // Site theme was fetched while a profile/community page was active — apply it now.
      ensureSiteThemeApplied();
      return;
    }
    if (theme?.primaryColor) {
      // Delay registration by 100ms to ensure LoadingScreen reads the previous page's theme
      const timer = setTimeout(() => {
        setCurrentTheme({
          type: 'site',
          colors: {
            primaryColor: theme.primaryColor,
            secondaryColor: theme.secondaryColor,
            textColor: theme.textColor,
            accentColor: theme.accentColor
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [theme?.primaryColor, theme?.secondaryColor, theme?.textColor, theme?.accentColor, siteCustomization, setCurrentTheme, ensureSiteThemeApplied]);

  useEffect(() => {
    const handleThemeUpdate = (event: any) => {
      const { animations, animationsEnabled: enabled, customization, baseTheme } = event.detail;

      // Merge incoming partial animations with full defaults from the appropriate theme
      if (animations) {
        // Use baseTheme from event if available, otherwise fall back to currentPreset
        const themeToUse = baseTheme || currentPreset;

        // Get the full default animations for the appropriate theme
        const defaultAnimations = getThemeAnimations(themeToUse);

        // Merge partial animation data with defaults
        const mergedAnimations = {
          // Merge characters array - match by id and merge properties
          characters: defaultAnimations.characters.map((defaultChar) => {
            const customChar = animations.characters?.find((c: any) => c.id === defaultChar.id);
            return customChar ? { ...defaultChar, ...customChar } : defaultChar;
          }),

          // Merge bubbles object
          bubbles: animations.bubbles
            ? { ...defaultAnimations.bubbles, ...animations.bubbles }
            : defaultAnimations.bubbles,

          // Merge decorations array - match by id and merge properties
          decorations: defaultAnimations.decorations.map((defaultDeco) => {
            const customDeco = animations.decorations?.find((d: any) => d.id === defaultDeco.id);
            return customDeco ? { ...defaultDeco, ...customDeco } : defaultDeco;
          }),
        };

        setThemeAnimations(mergedAnimations);
      }

      if (typeof enabled === 'boolean') {
        setAnimationsEnabled(enabled);
      }

      if (customization) {
        setCustomization(customization);
        if (typeof customization.focusAnimationEnabled === 'boolean') {
          setFocusAnimationEnabled(customization.focusAnimationEnabled);
        }
      }
    };

    window.addEventListener('siteThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('siteThemeUpdated', handleThemeUpdate);
  }, [currentPreset]);

  // Watch for siteCustomization updates from ThemeContext
  useEffect(() => {
    if (siteCustomization) {
            setCustomization(siteCustomization);
    }
  }, [siteCustomization]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Fetch posts for all tabs on mount and when tab changes
  useEffect(() => {
    async function fetchAllPosts() {
      try {
        // Determine which endpoint to use based on activeTab
        if (activeTab === 'joined' || activeTab === 'popular' || activeTab === 'all') {
          // Fetch community posts with tab parameter
          const allPostsResponse = await fetchWithAuth(`/api/communities/posts?tab=${activeTab}`, {
            credentials: 'include'
          });
          if (allPostsResponse.ok) {
            const data = await allPostsResponse.json();
            const mappedPosts = (data.posts || []).map((post: any) => normalizeLikeCounts({
              ...post,
              authorAvatar: resolveAvatar(post.authorAvatar || post.author?.profilePicture || post.author?.image)
            }));
            setPosts(mappedPosts);
          }
        }

        if (activeTab === 'friends' || activeTab === 'all') {
          // Fetch friends posts
          const friendsResponse = await fetchWithAuth('/api/communities/friends/activity', {
            credentials: 'include'
          });
          if (friendsResponse.ok) {
            const data = await friendsResponse.json();
            const mappedPosts = (data.posts || []).map((post: any) => normalizeLikeCounts({
              ...post,
              authorAvatar: resolveAvatar(post.authorAvatar || post.author?.profilePicture || post.author?.image)
            }));
            setFriendsPosts(mappedPosts);
          }
        }

        if (activeTab === 'following' || activeTab === 'all') {
          // Fetch following posts
          const followingResponse = await fetchWithAuth('/api/communities/following/activity', {
            credentials: 'include'
          });
          if (followingResponse.ok) {
            const data = await followingResponse.json();
            const mappedPosts = (data.posts || []).map((post: any) => normalizeLikeCounts({
              ...post,
              authorAvatar: resolveAvatar(post.authorAvatar || post.author?.profilePicture || post.author?.image)
            }));
            setFollowingPosts(mappedPosts);
          }
        }
      } catch (error) {
      }
    }

    fetchAllPosts();
  }, [activeTab, refetchTrigger]);

  // Search functionality for Navigate widget
  useEffect(() => {
    const performSearch = async () => {
      if (!sidebarSearchQuery.trim()) {
        setSidebarSearchPeople([]);
        setSidebarSearchCommunities([]);
        setSidebarSearchTags([]);
        return;
      }

      setIsSearching(true);
      try {
        if (sidebarSearchType === 'people') {
          const response = await fetchWithAuth(`/api/search/people?q=${encodeURIComponent(sidebarSearchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSidebarSearchPeople(data.people || []);
          }
        } else if (sidebarSearchType === 'communities') {
          const response = await fetchWithAuth(`/api/search/communities?q=${encodeURIComponent(sidebarSearchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSidebarSearchCommunities(data.communities || []);
          }
        } else if (sidebarSearchType === 'tags') {
          const response = await fetchWithAuth(`/api/search/tags?q=${encodeURIComponent(sidebarSearchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSidebarSearchTags(data.tags || []);
          }
        }
      } catch (error) {
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [sidebarSearchQuery, sidebarSearchType]);

  // Responsive check
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Wheel-based focus detection
  useEffect(() => {
    if (!focusAnimationEnabled || posts.length === 0) {
      setFocusedPostId(null);
      setFocusedPostIndex(-1);
      return;
    }

    const handleWheel = useCallback((e: WheelEvent) => {
      const direction = e.deltaY > 0 ? 1 : -1;
      const currentIndex = currentFocusIndexRef.current;

      // Check if we're at the edges
      const atTop = currentIndex === 0 && direction === -1;
      const atBottom = currentIndex === posts.length - 1 && direction === 1;

      // Allow normal scrolling at edges
      if (atTop || atBottom) {
        return;
      }

      e.preventDefault();

      // Update immediately without throttle
      const newIndex = currentIndex + direction;
      currentFocusIndexRef.current = newIndex;

      const focusedPost = posts[newIndex];
      if (focusedPost) {
        setFocusedPostId(focusedPost.id);
        setFocusedPostIndex(newIndex);
      }
    }, []); // TODO: Add dependencies

    // Initialize first post as focused (only if not already set)
    if (currentFocusIndexRef.current === -1 || currentFocusIndexRef.current >= posts.length) {
      currentFocusIndexRef.current = 0;
    }

    if (posts[currentFocusIndexRef.current]) {
      setFocusedPostId(posts[currentFocusIndexRef.current].id);
      setFocusedPostIndex(currentFocusIndexRef.current);
    }

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [focusAnimationEnabled, posts]);

  const likePost = async (postId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    // Find the post to get its communityId and type
    const targetPost = posts.find(p => p.id === postId);
    if (!targetPost) return;

    // Capture initial state before any updates
    const wasLiked = targetPost.liked;
    const initialLikes = targetPost.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Determine the correct API endpoint based on post type
    const endpoint = targetPost.postType === 'forum'
      ? `/api/communities/${targetPost.communityId}/threads/${postId}/like`
      : `/api/communities/${targetPost.communityId}/posts/${postId}/like`;

    // Optimistic update
    setPosts((prev) =>
      prev.map((post) => {
        if (post.id === postId) {
          return {
            ...post,
            liked: newLiked,
            likes: newLikes,
          };
        }
        return post;
      })
    );

    // Emit local event for same-page sync
    postEventBus.emitLike(postId, newLiked, newLikes);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state on error
        setPosts((prev) =>
          prev.map((post) => {
            if (post.id === postId) {
              return {
                ...post,
                liked: wasLiked,
                likes: initialLikes,
              };
            }
            return post;
          })
        );
        // Emit revert event
        postEventBus.emitLike(postId, wasLiked, initialLikes);
      }
    } catch (error) {
      // Revert to initial state on error
      setPosts((prev) =>
        prev.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              liked: wasLiked,
              likes: initialLikes,
            };
          }
          return post;
        })
      );
      // Emit revert event
      postEventBus.emitLike(postId, wasLiked, initialLikes);
    }
  };

  const dislikePost = async (postId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    // Find the post to get its communityId and type
    const targetPost = posts.find(p => p.id === postId);
    if (!targetPost) return;

    // Capture initial state before any updates
    const wasDisliked = targetPost.disliked;
    const initialDislikes = targetPost.dislikes || 0;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Determine the correct API endpoint based on post type
    const endpoint = targetPost.postType === 'forum'
      ? `/api/communities/${targetPost.communityId}/threads/${postId}/dislike`
      : `/api/communities/${targetPost.communityId}/posts/${postId}/dislike`;

    // Optimistic update
    setPosts((prev) =>
      prev.map((post) => {
        if (post.id === postId) {
          return {
            ...post,
            disliked: newDisliked,
            dislikes: newDislikes,
          };
        }
        return post;
      })
    );

    // Emit local event for same-page sync
    postEventBus.emitDislike(postId, newDisliked, newDislikes);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state on error
        setPosts((prev) =>
          prev.map((post) => {
            if (post.id === postId) {
              return {
                ...post,
                disliked: wasDisliked,
                dislikes: initialDislikes,
              };
            }
            return post;
          })
        );
        // Emit revert event
        postEventBus.emitDislike(postId, wasDisliked || false, initialDislikes);
      }
    } catch (error) {
      // Revert to initial state on error
      setPosts((prev) =>
        prev.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              disliked: wasDisliked,
              dislikes: initialDislikes,
            };
          }
          return post;
        })
      );
      // Emit revert event
      postEventBus.emitDislike(postId, wasDisliked || false, initialDislikes);
    }
  };

  const likeComment = async (commentId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    // Find the post and comment to capture initial state
    let targetPost: CommunityPost | undefined;
    let targetComment: Reply | undefined;

    for (const post of posts) {
      const comment = post.replies?.find(c => c.id === commentId);
      if (comment) {
        targetPost = post;
        targetComment = comment;
        break;
      }
    }

    if (!targetPost || !targetComment) return;

    // Capture initial state before any updates
    const wasLiked = targetComment.liked || false;
    const initialLikes = targetComment.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPosts((prev) =>
      prev.map((post) => {
        if (post.id === targetPost!.id && post.replies) {
          return {
            ...post,
            replies: post.replies.map(comment =>
              comment.id === commentId
                ? { ...comment, liked: newLiked, likes: newLikes }
                : comment
            ),
          };
        }
        return post;
      })
    );

    // Emit event for same-page sync
    commentEventBus.emitLike(parseInt(commentId), newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${targetPost.communityId}/posts/${targetPost.id}/replies/${commentId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts((prev) =>
          prev.map((post) => {
            if (post.id === targetPost!.id && post.replies) {
              return {
                ...post,
                replies: post.replies.map(comment =>
                  comment.id === commentId
                    ? { ...comment, liked: wasLiked, likes: initialLikes }
                    : comment
                ),
              };
            }
            return post;
          })
        );
        // Emit revert event
        commentEventBus.emitLike(parseInt(commentId), wasLiked, initialLikes);
      }
    } catch (error) {
      // Revert on error
      setPosts((prev) =>
        prev.map((post) => {
          if (post.id === targetPost!.id && post.replies) {
            return {
              ...post,
              replies: post.replies.map(comment =>
                comment.id === commentId
                  ? { ...comment, liked: wasLiked, likes: initialLikes }
                  : comment
              ),
            };
          }
          return post;
        })
      );
      // Emit revert event
      commentEventBus.emitLike(parseInt(commentId), wasLiked, initialLikes);
    }
  };

  const dislikeComment = async (commentId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    // Find the post and comment to capture initial state
    let targetPost: CommunityPost | undefined;
    let targetComment: Reply | undefined;

    for (const post of posts) {
      const comment = post.replies?.find(c => c.id === commentId);
      if (comment) {
        targetPost = post;
        targetComment = comment;
        break;
      }
    }

    if (!targetPost || !targetComment) return;

    // Capture initial state before any updates
    const wasDisliked = targetComment.disliked || false;
    const initialDislikes = targetComment.dislikes;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPosts((prev) =>
      prev.map((post) => {
        if (post.id === targetPost!.id && post.replies) {
          return {
            ...post,
            replies: post.replies.map(comment =>
              comment.id === commentId
                ? { ...comment, disliked: newDisliked, dislikes: newDislikes }
                : comment
            ),
          };
        }
        return post;
      })
    );

    // Emit event bus for same-page sync
    commentEventBus.emitDislike(parseInt(commentId), newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${targetPost.communityId}/posts/${targetPost.id}/replies/${commentId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts((prev) =>
          prev.map((post) => {
            if (post.id === targetPost!.id && post.replies) {
              return {
                ...post,
                replies: post.replies.map(comment =>
                  comment.id === commentId
                    ? { ...comment, disliked: wasDisliked, dislikes: initialDislikes }
                    : comment
                ),
              };
            }
            return post;
          })
        );
        commentEventBus.emitDislike(parseInt(commentId), wasDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert on error
      setPosts((prev) =>
        prev.map((post) => {
          if (post.id === targetPost!.id && post.replies) {
            return {
              ...post,
              replies: post.replies.map(comment =>
                comment.id === commentId
                  ? { ...comment, disliked: wasDisliked, dislikes: initialDislikes }
                  : comment
              ),
            };
          }
          return post;
        })
      );
      commentEventBus.emitDislike(parseInt(commentId), wasDisliked, initialDislikes);
    }
  };

  const handleCreatePost = useCallback(async (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
    communityId?: string;
    postType?: 'community' | 'forum';
    visibility?: 'public' | 'members-only';
  }) => {
    if (!postData.content.trim() || !postData.communityId) return;

    setIsPosting(true);

    try {
      const response = await fetchWithAuth(`/api/communities/${postData.communityId}/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: postData.title.trim() || undefined,
          content: postData.content,
          tags: postData.tags,
          postType: postData.postType || 'community',
          mediaUrl: postData.mediaUrl || undefined,
          mediaPosition: postData.mediaPosition,
          visibility: postData.visibility || 'public',
        }),
      });

      if (response.ok) {
        // Trigger refetch
        setRefetchTrigger(prev => prev + 1);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create post');
      }
    } catch (error) {
      alert('Failed to create post');
    } finally {
      setIsPosting(false);
    }
  }, []); // TODO: Add dependencies

  const handleToggleFollowTag = useCallback(async (tag: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking follow button

    const isCurrentlyFollowing = followedTags.includes(tag);
    const newFollowingState = !isCurrentlyFollowing;

    // Optimistically update UI
    setFollowedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );

    try {
      const response = await fetchWithAuth(`/api/tags/${tag}/follow`, {
        method: newFollowingState ? 'POST' : 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert on error
        setFollowedTags(prev =>
          prev.includes(tag)
            ? prev.filter(t => t !== tag)
            : [...prev, tag]
        );
      }
    } catch (error) {
      // Revert on error
      setFollowedTags(prev =>
        prev.includes(tag)
          ? prev.filter(t => t !== tag)
          : [...prev, tag]
      );
    }
  }, []); // TODO: Add dependencies

  const handleRepost = useCallback((postId: string) => {
    setRepostPostId(postId);
    setShowRepostModal(true);
  }, []); // TODO: Add dependencies

  const handleSubmitRepost = useCallback(async () => {
    if (!currentUser || !repostPostId) return;

    setIsReposting(true);
    try {
      const post = posts.find(p => p.id === repostPostId);
      if (!post) return;

      const response = await fetchWithAuth(`/api/posts/${repostPostId}/repost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: repostComment || undefined })
      });

      if (response.ok) {
        setShowRepostModal(false);
        setRepostComment('');
        setRepostPostId(null);
        window.location.reload();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to repost');
      }
    } catch (_error) {
      alert('Network error. Please try again.');
    }
    setIsReposting(false);
  }, []); // TODO: Add dependencies

  const handleShare = useCallback((postId: string) => {
    setSharePostId(postId);
    setShowShareModal(true);
  }, []); // TODO: Add dependencies

  const handleCopyLink = useCallback(async () => {
    if (!sharePostId) return;
    const post = posts.find(p => p.id === sharePostId);
    if (!post) return;
    const url = `${window.location.origin}/communities/${post.communityId}/posts/${sharePostId}`;

    try {
      await navigator.clipboard.writeText(url);

      // Increment link share count
      await fetchWithAuth(`/api/communities/${post.communityId}/posts/${sharePostId}/link-share`, {
        method: 'POST',
        credentials: 'include'
      });

      alert('Link copied to clipboard!');
      setShowShareModal(false);
      setSharePostId(null);
    } catch (err) {
      alert('Failed to copy link');
    }
  }, []); // TODO: Add dependencies

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Fetch fresh posts data
      const response = await fetchWithAuth('/api/communities/posts', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPosts((data.posts || []).map(normalizeLikeCounts));
      }
    } catch (error) {
    }
    // Brief delay to show loading screen
    setTimeout(() => {
      setIsRefreshing(false);
    }, 300);
  }, []); // TODO: Add dependencies

  const handleEditPost = useCallback((post: CommunityPost) => {
    setEditingPost(post as any);
    setShowModerateMenu(null);
  }, []); // TODO: Add dependencies

  const handleSaveEdit = useCallback(async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!editingPost) return;
    if (!currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const editorType = getEditorType(editingPost, permUser);
    const endpoint = `/api/moderation/communities/${(editingPost as any).communityId}/posts/${editingPost.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        const mappedEditorType = editorType === 'moderator' ? 'mod' : editorType === 'community_moderator' ? 'community_mod' : editorType;
        setPosts(prev => prev.map(p => p.id === editingPost.id ? {
          ...p,
          ...updatedData,
          editedBy: currentUser.id.toString(),
          editedByType: mappedEditorType as 'user' | 'admin' | 'mod' | 'community_admin' | 'community_mod',
          editedAt: new Date().toISOString()
        } : p));
        setEditingPost(null);
      }
    } catch (error) {
      throw error;
    }
  }, []); // TODO: Add dependencies

  const handlePinPost = useCallback(async (post: CommunityPost) => {
    if (!currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const pinnerType = getPinnerType(post as any, permUser);
    const endpoint = `/api/moderation/communities/${post.communityId}/posts/${post.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const mappedPinnerType = pinnerType === 'moderator' ? 'mod' : pinnerType === 'community_moderator' ? 'community_mod' : pinnerType;
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinned: data.pinned,
          pinnedBy: data.pinned ? currentUser.id.toString() : undefined,
          pinnedByType: data.pinned ? mappedPinnerType as 'admin' | 'mod' | 'community_admin' | 'community_mod' : undefined,
          pinnedAt: data.pinned ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const handlePinToFeed = useCallback(async (post: CommunityPost) => {
    if (!(currentUser as any).isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${post.id}/pin-to-feed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinnedToFeed: data.pinnedToFeed,
          pinnedToFeedAt: data.pinnedToFeed ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const handleDeletePost = useCallback(async (post: CommunityPost) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const endpoint = `/api/moderation/communities/${post.communityId}/posts/${post.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setPosts(prev => prev.filter(p => p.id !== post.id));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = useCallback((notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`);
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed');
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  }, []); // TODO: Add dependencies

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPostStyle = (postIndex: number) => {
    // Calculate the container width (32vw)
    const containerWidth = window.innerWidth * 0.32;

    if (!focusAnimationEnabled || focusedPostIndex === -1) {
      // Focus animation is disabled - use full container width
      return {
        width: `${containerWidth}px`,
        maxWidth: `${containerWidth}px`,
        transform: 'scale(1)',
        opacity: 1,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      };
    }

    const distance = Math.abs(postIndex - focusedPostIndex);
    const baseWidth = containerWidth; // Use container width as base

    if (distance === 0) {
      return {
        width: `${baseWidth}px`,
        maxWidth: `${baseWidth}px`,
        transform: 'scale(1.02)',
        opacity: 1,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      };
    }

    const widthPercent = Math.max(72, 100 - (distance * 12));
    const calculatedWidth = (baseWidth * widthPercent) / 100;
    const scale = Math.max(0.95, 1 - (distance * 0.02));
    const opacity = Math.max(0.6, 1 - (distance * 0.12));

    return {
      width: `${calculatedWidth}px`,
      maxWidth: `${calculatedWidth}px`,
      transform: `scale(${scale})`,
      opacity,
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    };
  };

  const joinedCommunities = communities.filter(c => c.isMember);

  // Shuffle and get 10 random communities for discover widget
  const [discoverCommunities, setDiscoverCommunities] = useState<typeof communities>([]);
  const [discoverKey, setDiscoverKey] = useState(0);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    const nonMemberCommunities = communities.filter(c => !c.isMember);
    const shuffled = [...nonMemberCommunities].sort(() => 0.5 - Math.random());
    setDiscoverCommunities(shuffled.slice(0, 10));
  }, [communities, discoverKey]);

  const refreshDiscoverCommunities = () => {
    setDiscoverKey(prev => prev + 1);
  };

  const handleLogout = useCallback(async () => {
    logout();
  }, [logout]);

  const navItems = [
    { label: 'Home', path: '/feed' },
    { label: 'Friends', path: '/friends' },
    { label: 'Messages', path: '/messages' },
    { label: 'Community', path: '/community' },
    { label: 'Ads', path: '/ads' },
    { label: 'Marketplace', path: '/marketplace' }
  ];

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'joined', label: 'Joined' },
    { id: 'friends', label: 'Friends' },
    { id: 'following', label: 'Following' },
    { id: 'popular', label: 'Popular' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest', icon: Calendar },
    { value: 'oldest', label: 'Oldest', icon: Clock },
    { value: 'popular', label: 'Popular', icon: TrendingUp },
    { value: 'unpopular', label: 'Unpopular', icon: TrendingDown },
    { value: 'controversial', label: 'Most Controversial', icon: Flame }
  ];

  const currentSortOption = sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

  const handleToggleShowSortMenu = useCallback(() => {
    setShowSortMenu(prev => !prev);
  }, []);
  const handleToggleShowSiteCustomizer = useCallback(() => {
    setShowSiteCustomizer(prev => !prev);
  }, []);
  const handleCloseShowPostWidget = useCallback(() => {
    setShowPostWidget(false);
  }, []);
  const handleCloseShowRepostModal = useCallback(() => {
    setShowRepostModal(false);
  }, []);
  const handleCloseShowShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);
  const handleCloseShowNotifications = useCallback(() => {
    setShowNotifications(false);
  }, []);
  const handleCloseShowSortMenu = useCallback(() => {
    setShowSortMenu(false);
  }, []);
  const handleCloseShowSidebarSearchResults = useCallback(() => {
    setShowSidebarSearchResults(false);
  }, []);
  const handleCloseShowSiteCustomizer = useCallback(() => {
    setShowSiteCustomizer(false);
  }, []);
  const handleCloseShowCreateCommunityModal = useCallback(() => {
    setShowCreateCommunityModal(false);
  }, []);
  const handleCloseShowModerateMenu = useCallback(() => {
    setShowModerateMenu(null);
  }, []);
  const handleOpenShowRepostModal = useCallback(() => {
    setShowRepostModal(true);
  }, []);
  const handleOpenShowShareModal = useCallback(() => {
    setShowShareModal(true);
  }, []);
  const handleOpenShowPostWidget = useCallback(() => {
    setShowPostWidget(true);
  }, []);
  const handleOpenShowCreateCommunityModal = useCallback(() => {
    setShowCreateCommunityModal(true);
  }, []);
  const handleOpenShowSidebarSearchResults = useCallback(() => {
    setShowSidebarSearchResults(true);
  }, []);

  // ─── Render modals (shared between desktop & mobile) ───
  const renderModals = () => {
    const modals = (
      <>
        {/* Post Creation Modal */}
        <CommunityPostModal
          isOpen={showPostWidget}
          onClose={() => setShowPostWidget(false)}
          onSubmit={handleCreatePost}
          currentUser={currentUser}
          theme={localTheme}
          scale={scale}
          scaleW={scaleW}
          scaleH={scaleH}
          getCardBackground={getCardBackground}
          customization={customization}
          communities={joinedCommunities}
        />

        {currentUser && (
          <SiteThemeCustomizer
            isOpen={showSiteCustomizer}
            onClose={() => { requestAnimationFrame(() => setShowSiteCustomizer(false)); }}
            userId={currentUser.id}
            initialCustomization={customization}
            initialAnimations={themeAnimations}
            initialAnimationsEnabled={animationsEnabled}
          />
        )}

        {editingPost && (
          <EditPostModal
            post={editingPost}
            isOpen={!!editingPost}
            onClose={() => setEditingPost(null)}
            onSave={handleSaveEdit}
          />
        )}
      </>
    );
    return effectiveIsMobile ? createPortal(modals, document.body) : modals;
  };

  // ─── Mobile Layout ───
  if (mobileMode) {

    // Filter posts based on active tab
    const getFilteredPosts = () => {
      let filteredPosts = posts;
      if (!isAuthenticated && (activeTab === 'joined' || activeTab === 'friends' || activeTab === 'following')) {
        return [];
      }
      switch(activeTab) {
        case 'joined':
        case 'popular':
          filteredPosts = posts;
          break;
        case 'friends':
          filteredPosts = friendsPosts;
          break;
        case 'following':
          filteredPosts = followingPosts;
          break;
        case 'all':
        default:
          filteredPosts = [...posts, ...friendsPosts, ...followingPosts]
            .filter((post, index, self) =>
              index === self.findIndex(p => p.id === post.id)
            );
          break;
      }
      // Apply sorting
      let sortedPosts = [...filteredPosts];
      if (activeTab === 'popular' && sortBy === 'newest') {
        sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
      } else {
        switch(sortBy) {
          case 'newest':
            sortedPosts = sortedPosts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            break;
          case 'oldest':
            sortedPosts = sortedPosts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            break;
          case 'popular':
            sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
            break;
          case 'unpopular':
            sortedPosts = sortedPosts.sort((a, b) => (b.dislikes || 0) - (a.dislikes || 0));
            break;
          case 'controversial':
            sortedPosts = sortedPosts.sort((a, b) => {
              const aC = Math.min(a.likes, a.dislikes || 0);
              const bC = Math.min(b.likes, b.dislikes || 0);
              return bC - aC;
            });
            break;
        }
      }
      return sortedPosts;
    };

    const mobilePosts = getFilteredPosts();

    return (
      <div style={{ background: getBackground(ensureThemeColors, customization, theme.appBg), height: '100%', overflow: 'hidden' }}>
        <SubPanelSwiper
          activePanel={{ left: 0, middle: 1, right: 2 }[currentSection]}
          onPanelChange={(i: number) => {
            const section = (['left', 'middle', 'right'] as const)[i];
            setCurrentSection(section);
          }}
          screenWidth={mobileScreenWidth}
        >
          {[
            /* ── Left Panel: Share + Create Community buttons ── */
            <div key="left" className="p-4 flex flex-col items-center gap-3" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top) + 1rem)' }}>
              {isAuthenticated && currentUser && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => {
                      setIsShareAnimating(true);
                      setTimeout(() => {
                        setShowPostWidget(true);
                        setIsShareAnimating(false);
                      }, 300);
                    });
                  }}
                  className="rounded-lg shadow-lg font-semibold overflow-hidden"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor,
                    padding: '16px 48px',
                    fontSize: '18px',
                  }}
                >
                  <span style={{
                    display: 'inline-block',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: isShareAnimating ? 0 : 1,
                    transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
                  }}>
                    {showPostWidget ? 'Post' : 'Share'}
                  </span>
                </button>
              )}

              {currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin) && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => setShowCreateCommunityModal(true));
                  }}
                  className="rounded-lg shadow-lg font-semibold whitespace-nowrap"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor,
                    padding: '16px 48px',
                    fontSize: '18px',
                  }}
                >
                  Create Community
                </button>
              )}

              {currentUser && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => setShowSiteCustomizer(true));
                  }}
                  className="rounded-lg font-semibold w-full flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor,
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    minHeight: '48px',
                  }}
                >
                  Customize Theme
                </button>
              )}
            </div>,

            /* ── Center Panel: Tab Bar + Sort + Wavy Divider + Posts ── */
            <div key="center" ref={pullToRefresh.containerRef} className="flex flex-col items-center w-full px-2 h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Pull-to-refresh indicator */}
              {(pullToRefresh.isPulling || pullToRefresh.isRefreshing) && (
                <div className="flex items-center justify-center shrink-0" style={{
                  height: `${pullToRefresh.pullDistance}px`,
                  transition: pullToRefresh.isPulling ? 'none' : 'height 0.3s ease-out',
                  overflow: 'hidden',
                }}>
                  <div className={`w-6 h-6 rounded-full border-2 border-t-transparent ${pullToRefresh.isRefreshing ? 'animate-spin' : ''}`}
                    style={{
                      borderColor: localTheme.accentColor,
                      borderTopColor: 'transparent',
                      opacity: Math.min(pullToRefresh.pullDistance / 60, 1),
                    }}
                  />
                </div>
              )}
              <div style={pullToRefresh.pullIndicatorStyle} className="w-full flex flex-col items-center">
              {/* Feed Tabs Card */}
              <div className="rounded-lg backdrop-blur-sm border mb-3 w-full" style={{
                background: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                borderColor: `${localTheme.accentColor}40`,
                padding: '0 12px',
              }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          requestAnimationFrame(() => setActiveTab(tab.id));
                        }}
                        className="font-semibold transition-all whitespace-nowrap"
                        style={{
                          color: activeTab === tab.id ? localTheme.textColor : `${localTheme.textColor}99`,
                          borderBottom: activeTab === tab.id ? `2px solid ${localTheme.accentColor}` : `2px solid transparent`,
                          paddingBottom: '4px',
                          paddingTop: '8px',
                          fontSize: '13px',
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  {/* Sort Dropdown */}
                  <div className="relative" style={{ zIndex: 41 }}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        requestAnimationFrame(() => setShowSortMenu(prev => !prev));
                      }}
                      className="flex items-center rounded-lg font-medium"
                      style={{
                        gap: '4px',
                        padding: '4px 8px',
                        background: themeColors.dropdownButtonBg,
                        border: 'none',
                        color: localTheme.textColor,
                        fontSize: '12px',
                      }}
                    >
                      <currentSortOption.icon size={14} />
                      <ChevronDown size={14} className={showSortMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
                    </button>
                    {showSortMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowSortMenu(false)}
                        />
                        <div className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl z-50" style={{
                          background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                          border: `1px solid ${localTheme.accentColor}40`,
                          marginTop: '4px',
                          minWidth: '140px',
                        }}>
                          {sortOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                requestAnimationFrame(() => {
                                  setSortBy(option.value);
                                  setShowSortMenu(false);
                                });
                              }}
                              className="flex items-center gap-2 w-full text-left transition-colors"
                              style={{
                                padding: '10px 14px',
                                backgroundColor: sortBy === option.value ? `${localTheme.accentColor}30` : 'transparent',
                                color: localTheme.textColor,
                                fontSize: '13px',
                              }}
                            >
                              <option.icon size={14} style={{ opacity: 0.7 }} />
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Wavy Divider */}
              <div className="w-full" style={{ marginBottom: '16px' }}>
                <svg width="100%" height="12" viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
                  <path
                    d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10"
                    fill="none"
                    stroke={localTheme.accentColor}
                    strokeWidth="2"
                    opacity="0.4"
                  />
                </svg>
              </div>

              {/* Posts */}
              {isRefreshing ? (
                <SoftLoad message="Refreshing..." />
              ) : !isAuthenticated && (activeTab === 'joined' || activeTab === 'friends' || activeTab === 'following') ? (
                <div className="rounded-lg backdrop-blur-sm border text-center" style={{
                  background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                  borderColor: `${localTheme.accentColor}40`,
                  padding: '24px 16px',
                  width: '100%',
                }}>
                  <div style={{ color: localTheme.textColor, fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Login to View More
                  </div>
                  <div style={{ color: localTheme.textColor, opacity: 0.7, fontSize: '14px' }}>
                    Sign in to see posts from your {activeTab === 'joined' ? 'joined communities' : activeTab === 'friends' ? 'friends' : 'followed users'}
                  </div>
                </div>
              ) : mobilePosts.length === 0 ? (
                <div className="rounded-lg backdrop-blur-sm border text-center" style={{
                  background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                  borderColor: `${localTheme.accentColor}40`,
                  padding: '32px 16px',
                  width: '100%',
                }}>
                  <div style={{ color: localTheme.textColor, opacity: 0.5, fontSize: '14px' }}>No posts yet</div>
                </div>
              ) : (
                <div className="w-full flex flex-col gap-3">
                  {mobilePosts.map((post) => (
                    <button
                      key={post.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const url = post.postType === 'forum' && post.communitySlug && post.slug
                          ? getForumPostUrl(post.communitySlug, post.slug)
                          : post.communitySlug && post.slug
                            ? getCommunityPostUrl(post.communitySlug, post.slug)
                            : `/community/${post.communitySlug || post.communityId}`;
                        requestAnimationFrame(() => navigate(url));
                      }}
                      className="rounded-lg backdrop-blur-sm border text-left w-full"
                      style={{
                        background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                        borderColor: `${localTheme.accentColor}40`,
                        padding: '12px 14px',
                      }}
                    >
                      <div className="pointer-events-none">
                        {/* Community + Author */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{
                            background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`,
                          }}>
                            {post.authorAvatar ? (
                              <img src={resolveAvatar(post.authorAvatar) || ''} alt="" className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <Users size={14} style={{ color: localTheme.textColor }} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate" style={{ color: localTheme.accentColor }}>
                              {post.communityName}
                            </div>
                            <div className="text-xs truncate" style={{ color: localTheme.textColor, opacity: 0.5 }}>
                              {post.author}
                            </div>
                          </div>
                        </div>
                        {/* Title */}
                        {post.title && (
                          <h4 className="text-sm font-semibold mb-1 line-clamp-2" style={{ color: localTheme.textColor }}>
                            {post.title}
                          </h4>
                        )}
                        {/* Content preview */}
                        <p className="text-xs line-clamp-3 mb-2" style={{ color: localTheme.textColor, opacity: 0.7 }}>
                          {post.content}
                        </p>
                        {/* Media */}
                        {(post.mediaUrl || post.imageUrl) && (
                          <div className="rounded-lg overflow-hidden mb-2" style={{ maxHeight: '200px' }}>
                            <img
                              src={post.mediaUrl || post.imageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        )}
                        {/* Stats */}
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1 text-xs" style={{ color: localTheme.textColor, opacity: 0.4 }}>
                            <Heart size={12} /> {post.likes}
                          </span>
                          <span className="flex items-center gap-1 text-xs" style={{ color: localTheme.textColor, opacity: 0.4 }}>
                            <MessageSquare size={12} /> {post.comments}
                          </span>
                          <span className="flex items-center gap-1 text-xs" style={{ color: localTheme.textColor, opacity: 0.4 }}>
                            <Share2 size={12} /> {post.shares}
                          </span>
                        </div>

                        {/* Comment Previews */}
                        {post.replies && post.replies.length > 0 && (() => {
                          const previewComments = selectPreviewComments(post.replies);
                          return (
                            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                              <div className="flex flex-col gap-2.5">
                                {previewComments.map((reply) => (
                                  <div key={reply.id} className="flex gap-2">
                                    <div
                                      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                                      style={{ background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)` }}
                                    >
                                      {reply.author.profilePicture ? (
                                        <img src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture} alt="" className="w-full h-full rounded-full object-cover" />
                                      ) : (
                                        <PixelatedTurtle size={12} color={localTheme.accentColor} />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5 mb-0.5">
                                        <span className="text-xs font-semibold truncate" style={{ color: localTheme.textColor }}>
                                          {reply.author.profileName || reply.author.username}
                                        </span>
                                        <span className="text-xs shrink-0" style={{ color: localTheme.textColor + '66' }}>
                                          {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                      </div>
                                      <p className="text-xs line-clamp-2" style={{ color: localTheme.textColor + 'cc' }}>{reply.content}</p>
                                      <div className="flex items-center gap-3 mt-1 pointer-events-auto">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); likeComment(reply.id, e); }}
                                          className="flex items-center gap-1"
                                          style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '66' }}
                                        >
                                          <Heart size={11} className={reply.liked ? "fill-current" : ""} />
                                          <span className="text-xs">{reply.likes}</span>
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); dislikeComment(reply.id, e); }}
                                          className="flex items-center gap-1"
                                          style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '66' }}
                                        >
                                          <ThumbsDown size={11} className={reply.disliked ? "fill-current" : ""} />
                                          <span className="text-xs">{reply.dislikes}</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {post.comments > 3 && (
                                <button
                                  className="text-xs mt-2 pointer-events-auto"
                                  style={{ color: localTheme.accentColor }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const url = post.postType === 'forum' && post.communitySlug && post.slug
                                      ? getForumPostUrl(post.communitySlug, post.slug)
                                      : post.communitySlug && post.slug
                                        ? getCommunityPostUrl(post.communitySlug, post.slug)
                                        : `/community/${post.communitySlug || post.communityId}`;
                                    requestAnimationFrame(() => navigate(url));
                                  }}
                                >
                                  View all {post.comments} comments
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="h-24" />
              </div>{/* close pullIndicatorStyle wrapper */}
            </div>,

            /* ── Right Panel: Widgets stacked vertically ── */
            <div key="right" className="pt-6 px-4 flex flex-col gap-4">
              {/* Customize Theme */}
              {currentUser && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => setShowSiteCustomizer(true));
                  }}
                  className="rounded-lg font-semibold w-full flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor,
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    minHeight: '48px',
                  }}
                >
                  Customize Theme
                </button>
              )}

              {/* Navigate Widget */}
              <div className="rounded-lg backdrop-blur-sm border" style={{
                background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                borderColor: `${localTheme.accentColor}40`,
                padding: 0,
              }}>
                <h3 className="font-bold text-center border-b" style={{
                  color: localTheme.textColor,
                  borderColor: `${localTheme.accentColor}40`,
                  fontSize: '16px',
                  padding: '12px',
                }}>Navigate</h3>
                <div style={{ padding: '12px' }}>
                  <input
                    type="text"
                    placeholder="Navigate..."
                    value={sidebarSearchQuery}
                    onChange={(e) => setSidebarSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && sidebarSearchQuery.trim()) {
                        setShowSidebarSearchResults(true);
                      }
                    }}
                    className="w-full rounded-lg outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: localTheme.textColor,
                      border: `1px solid ${localTheme.accentColor}40`,
                      padding: '12px',
                      fontSize: '14px',
                      marginBottom: '12px',
                    }}
                  />
                  <div className="flex gap-0 rounded-lg overflow-hidden" style={{ border: `1px solid ${localTheme.accentColor}40` }}>
                    {(['people', 'communities', 'tags'] as const).map(type => (
                      <button
                        key={type}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          requestAnimationFrame(() => setSidebarSearchType(type));
                        }}
                        className="flex-1 font-semibold transition-all"
                        style={{
                          backgroundColor: sidebarSearchType === type ? localTheme.accentColor : 'rgba(255, 255, 255, 0.05)',
                          color: localTheme.textColor,
                          padding: '8px',
                          fontSize: '13px',
                          borderRight: type !== 'tags' ? `1px solid ${localTheme.accentColor}40` : undefined,
                        }}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Discover Widget */}
              <div className="rounded-lg backdrop-blur-sm border" style={{
                background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                borderColor: `${localTheme.accentColor}40`,
                padding: 0,
              }}>
                <div className="flex items-center justify-between" style={{
                  borderBottom: `1px solid ${localTheme.accentColor}40`,
                  padding: '12px 16px',
                }}>
                  <h3 className="font-bold flex items-center gap-2" style={{ color: localTheme.textColor, fontSize: '16px' }}>
                    <Compass size={18} /> Discover
                  </h3>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  {discoverCommunities.length === 0 ? (
                    <p style={{ color: localTheme.textColor + '99', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>
                      No communities to discover
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {discoverCommunities.map((community) => (
                        <button
                          key={community.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            requestAnimationFrame(() => navigate(`/community/${community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-')}`));
                          }}
                          className="flex items-center gap-3 p-2 rounded-lg text-left"
                        >
                          <div className="rounded-full flex items-center justify-center shrink-0" style={{
                            background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`,
                            width: '36px', height: '36px',
                          }}>
                            {community.avatar ? (
                              <img src={community.avatar} alt="" className="w-full h-full rounded-full object-cover pointer-events-none" />
                            ) : (
                              <Users size={16} style={{ color: localTheme.textColor }} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pointer-events-none">
                            <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>{community.name}</div>
                            <div className="text-xs" style={{ color: localTheme.textColor + '99' }}>{community.memberCount} members</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Joined Communities Widget */}
              <div className="rounded-lg backdrop-blur-sm border" style={{
                background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                borderColor: `${localTheme.accentColor}40`,
                padding: 0,
              }}>
                <h3 className="font-bold text-center" style={{
                  color: localTheme.textColor,
                  borderBottom: `1px solid ${localTheme.accentColor}40`,
                  padding: '12px',
                  fontSize: '16px',
                }}>Joined</h3>
                <div style={{ padding: '12px 16px' }}>
                  {joinedCommunities.length === 0 ? (
                    <p style={{ color: localTheme.textColor + '99', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>
                      No joined communities yet
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {joinedCommunities.map((community) => (
                        <button
                          key={community.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            requestAnimationFrame(() => navigate(`/community/${community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-')}`));
                          }}
                          className="flex items-center gap-3 p-2 rounded-lg text-left"
                        >
                          <div className="rounded-full flex items-center justify-center shrink-0" style={{
                            background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`,
                            width: '36px', height: '36px',
                          }}>
                            {community.avatar ? (
                              <img src={community.avatar} alt="" className="w-full h-full rounded-full object-cover pointer-events-none" />
                            ) : (
                              <Users size={16} style={{ color: localTheme.textColor }} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pointer-events-none">
                            <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>{community.name}</div>
                            <div className="text-xs" style={{ color: localTheme.textColor + '99' }}>{community.memberCount} members</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="h-24" />
            </div>,
          ]}
        </SubPanelSwiper>
        {renderModals()}

        {/* Create Community Modal */}
        {showCreateCommunityModal && createPortal(
          <div className="fixed inset-0 z-[200] flex items-end justify-center" onClick={() => setShowCreateCommunityModal(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
              className="relative w-full max-w-lg rounded-t-2xl"
              style={{
                background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                border: `1px solid ${localTheme.accentColor}40`,
                borderBottom: 'none',
                maxHeight: '85vh',
                overflow: 'auto',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ padding: '20px' }}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold" style={{ color: localTheme.textColor }}>Create Community</h2>
                  <button onClick={() => setShowCreateCommunityModal(false)}>
                    <X size={24} style={{ color: localTheme.textColor }} />
                  </button>
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: localTheme.textColor }}>Name:</label>
                    <input type="text" value={newCommunityName} onChange={(e) => setNewCommunityName(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border outline-none"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: `${localTheme.accentColor}40`, color: localTheme.textColor }}
                      placeholder="Community name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: localTheme.textColor }}>Title:</label>
                    <input type="text" value={newCommunityTitle} onChange={(e) => setNewCommunityTitle(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border outline-none"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: `${localTheme.accentColor}40`, color: localTheme.textColor }}
                      placeholder="Community title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: localTheme.textColor }}>Bio:</label>
                    <textarea value={newCommunityBio} onChange={(e) => setNewCommunityBio(e.target.value)} rows={3}
                      className="w-full px-4 py-2 rounded-lg border outline-none resize-none"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: `${localTheme.accentColor}40`, color: localTheme.textColor }}
                      placeholder="Community bio" />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={newCommunityIsPublic} onChange={(e) => setNewCommunityIsPublic(e.target.checked)}
                      className="w-5 h-5 rounded" style={{ accentColor: localTheme.accentColor }} />
                    <span className="text-sm" style={{ color: localTheme.textColor }}>Public Community</span>
                  </label>
                  <div className="flex gap-3">
                    <button onClick={() => { setNewCommunityName(''); setNewCommunityTitle(''); setNewCommunityBio(''); setNewCommunityIsPublic(true); setShowCreateCommunityModal(false); }}
                      className="px-6 py-3 rounded-lg font-semibold"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: localTheme.textColor }}>
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!newCommunityName.trim() || !newCommunityTitle.trim()) { alert('Please fill in name and title'); return; }
                        setIsCreatingCommunity(true);
                        try {
                          const { createCommunity } = await import('../../lib/api-client');
                          const community = await createCommunity({ name: newCommunityName, description: newCommunityBio || undefined, isPublic: newCommunityIsPublic });
                          setNewCommunityName(''); setNewCommunityTitle(''); setNewCommunityBio(''); setNewCommunityIsPublic(true); setShowCreateCommunityModal(false);
                          const slug = community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-');
                          navigate(`/community/${slug}`);
                        } catch (error) { alert(error instanceof Error ? error.message : 'Failed to create community'); }
                        finally { setIsCreatingCommunity(false); }
                      }}
                      disabled={isCreatingCommunity}
                      className="flex-1 py-3 rounded-lg font-semibold disabled:opacity-50"
                      style={{ backgroundColor: localTheme.accentColor, color: localTheme.textColor }}>
                      {isCreatingCommunity ? 'Creating...' : 'Create Community'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  return (
    <div style={{ background: getBackground(ensureThemeColors, customization, theme.appBg), minHeight: backgroundHeight }}>
      <style>{`
        .pixelated {
          image-rendering: pixelated;
          image-rendering: -moz-crisp-edges;
          image-rendering: crisp-edges;
        }
      `}</style>

      {/* Theme-Specific Floating Elements */}
      <AnimationLayer
        animations={themeAnimations}
        isMobile={false}
        bubbleColor={localTheme.accentColor}
        enabled={animationsEnabled}
      />

      {/* Top Navigation Bar */}
      <MainNavbar
        key={`navbar-${localTheme.navbarBg}`}
        currentPath="/community"
        onLogout={handleLogout}
        onSignIn={() => setShowLoginModal(true)}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        theme={localTheme}
        customization={customization}
        navbarBg={localTheme.navbarBg}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        getNotificationIcon={getNotificationIcon}
        getNotificationMessage={getNotificationMessage}
        formatNotificationTime={formatNotificationTime}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
      />

      {/* Page Header */}
      <div style={{ paddingTop: `${scaleH(24, scale.h)}px`, paddingBottom: `${scaleH(16, scale.h)}px` }}>
        <h1 className="font-bold text-center" style={{ color: localTheme.textColor, fontSize: `${scaleW(36, scale.w)}px` }}>Communities</h1>
      </div>

      {/* Main Layout - Three Column Structure */}
      <div ref={swipeRef} className={`mx-auto pb-8 w-full relative ${isMobile ? 'overflow-hidden' : ''}`}>
        {/* LEFT SECTION - Share Button, Create Community Button & Modals */}
        <div
          className="fixed transition-all duration-300"
          style={{
            left: `${scaleW(24, scale.w)}px`,
            top: `${scaleH(96, scale.h)}px`,
            zIndex: 40,
            opacity: isMobile ? (currentSection === 'left' ? 1 : 0) : 1,
            pointerEvents: isMobile ? (currentSection === 'left' ? 'auto' : 'none') : 'auto'
          }}>
          {/* Share Button (Animated) - Only show when authenticated */}
          {isAuthenticated && currentUser && (
            <button
              onClick={() => {
                setIsShareAnimating(true);
                setTimeout(() => {
                  setShowPostWidget(true);
                  setIsShareAnimating(false);
                }, 400);
              }}
              className="rounded-lg shadow-lg font-semibold overflow-hidden"
              style={{
                backgroundColor: localTheme.accentColor,
                color: localTheme.textColor,
                padding: `${scaleH(12, scale.h)}px ${scaleW(32, scale.w)}px`,
                fontSize: `${scaleW(18, scale.w)}px`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: showPostWidget ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: isShareAnimating ? 0 : 1,
                  transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
                }}
              >
                {showPostWidget ? 'Post' : 'Share'}
              </span>
            </button>
          )}

          {/* Create Community Button - To the right of Share button (Admin/Moderator Only) */}
          {currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin) && (
            <button
              onClick={handleOpenShowCreateCommunityModal}
              className="rounded-lg shadow-lg font-semibold whitespace-nowrap"
              style={{
                position: 'absolute',
                left: `${scaleW(200, scale.w)}px`,
                backgroundColor: localTheme.accentColor,
                color: localTheme.textColor,
                padding: `${scaleH(12, scale.h)}px ${scaleW(40, scale.w)}px`,
                fontSize: `${scaleW(18, scale.w)}px`,
              }}
            >
              Create Community
            </button>
          )}
        </div>

        {/* MIDDLE SECTION - Main Feed Column - Centered under "Communities" title */}
        <div
          className="w-[32vw] max-w-[900px] min-w-[500px] flex flex-col items-center transition-all duration-300"
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 40,
            opacity: isMobile ? (currentSection === 'middle' ? 1 : 0) : 1,
            pointerEvents: isMobile ? (currentSection === 'middle' ? 'auto' : 'none') : 'auto'
          }}>
            {/* Feed Tabs Card */}
            <div key={`tabbar-${localTheme.tabBarBg}`} className="rounded-lg backdrop-blur-sm border mb-6 sticky" style={{
              background: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
              borderColor: `${localTheme.accentColor}40`,
              zIndex: 40,
              width: 'fit-content',
              paddingLeft: `${scaleW(24, scale.w)}px`,
              paddingRight: `${scaleW(24, scale.w)}px`,
              top: `${scaleH(100, scale.h)}px`
            }}>
              <div className="flex items-center">
                <div className="flex items-center" style={{ gap: `${scaleW(16, scale.w)}px` }}>
                  {tabs.map((tab) => (
                    <button
                      key={`${tab.id}-${localTheme.tabBarBg}`}
                      onClick={() => setActiveTab(tab.id)}
                      className="font-semibold transition-all duration-300"
                      style={{
                        color: activeTab === tab.id ? localTheme.textColor : `${localTheme.textColor}99`,
                        borderBottom: activeTab === tab.id ? `${scaleH(2, scale.h)}px solid ${localTheme.accentColor}` : `${scaleH(2, scale.h)}px solid transparent`,
                        paddingBottom: `${scaleH(4, scale.h)}px`,
                        fontSize: `${scaleW(18, scale.w)}px`
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = localTheme.textColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = activeTab === tab.id ? localTheme.textColor : `${localTheme.textColor}99`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Sort Dropdown */}
                <div className="relative" style={{ zIndex: 41, marginLeft: `${scaleW(64, scale.w)}px` }}>
                  <button
                    onClick={handleToggleShowSortMenu}
                    className="flex items-center rounded-lg font-medium transition-all"
                    style={{
                      gap: `${scaleW(8, scale.w)}px`,
                      paddingLeft: `${scaleW(16, scale.w)}px`,
                      paddingRight: `${scaleW(16, scale.w)}px`,
                      background: themeColors.dropdownButtonBg,
                      border: 'none',
                      color: localTheme.textColor,
                      fontSize: `${scaleW(14, scale.w)}px`
                    }}
                  >
                    <currentSortOption.icon size={scaleW(16, scale.w)} />
                    <span>{currentSortOption.label}</span>
                    <ChevronDown size={scaleW(16, scale.w)} className={showSortMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
                  </button>

                  {showSortMenu && (
                    <>
                      <div
                        className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl"
                        style={{
                          backgroundColor: themeColors.dropdownMenuBg,
                          borderColor: localTheme.textColor,
                          border: '2px solid',
                          zIndex: 42,
                          marginTop: `${scaleH(8, scale.h)}px`,
                          minWidth: `${scaleW(160, scale.w)}px`
                        }}
                      >
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortMenu(false);
                            }}
                            className="flex items-center w-full text-left font-medium transition-all"
                            style={{
                              color: localTheme.textColor,
                              backgroundColor: option.value === sortBy
                                ? (customization.highlightColor || localTheme.accentColor)
                                : 'transparent',
                              gap: `${scaleW(8, scale.w)}px`,
                              paddingLeft: `${scaleW(16, scale.w)}px`,
                              paddingRight: `${scaleW(16, scale.w)}px`,
                              paddingTop: `${scaleH(12, scale.h)}px`,
                              paddingBottom: `${scaleH(12, scale.h)}px`,
                              fontSize: `${scaleW(14, scale.w)}px`
                            }}
                            onMouseEnter={(e) => {
                              if (option.value !== sortBy) {
                                const highlightColor = customization.highlightColor || localTheme.accentColor;
                                e.currentTarget.style.backgroundColor = highlightColor + '40';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (option.value !== sortBy) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <option.icon size={Math.round(scaleW(16, scale.w))} />
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 90 }}
                        onClick={handleCloseShowSortMenu}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Wavy Divider */}
            <div className="w-full" style={{ marginTop: `${scaleH(32, scale.h)}px`, marginBottom: `${scaleH(48, scale.h)}px` }}>
              <svg width="100%" height={scaleH(20, scale.h)} viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
                <path
                  d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10"
                  fill="none"
                  stroke={localTheme.accentColor}
                  strokeWidth={scaleW(2, scale.w)}
                  opacity="0.4"
                />
              </svg>
            </div>

            {/* Posts Display - full width */}
            {(() => {
              // Show soft loading indicator when refreshing
              if (isRefreshing) {
                return <SoftLoad message="Refreshing..." />;
              }

              // Filter posts based on active tab
              let filteredPosts = posts;
              // Show login prompt for joined, friends, and following tabs when not authenticated
              if (!isAuthenticated && (activeTab === 'joined' || activeTab === 'friends' || activeTab === 'following')) {
                return (
                  <div className="rounded-lg backdrop-blur-sm border text-center" style={{
                    background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                    borderColor: `${localTheme.accentColor}40`,
                    marginBottom: '2rem',
                    padding: `${scaleH(42, scale.h)}px ${scaleW(24, scale.w)}px`,
                  }}>
                    <div className="mb-6" style={{ color: localTheme.textColor, fontSize: `${scaleW(24, scale.w)}px`, fontWeight: 'bold' }}>
                      Login to View More
                    </div>
                    <div style={{ color: localTheme.textColor, opacity: 0.7, fontSize: `${scaleW(16, scale.w)}px`, lineHeight: '1.6' }}>
                      Sign in to see posts from your {activeTab === 'joined' ? 'joined communities' : activeTab === 'friends' ? 'friends' : 'followed users'}
                    </div>
                  </div>
                );
              }

              // Backend now handles all filtering based on tab parameter
              switch(activeTab) {
                case 'joined':
                case 'popular':
                  filteredPosts = posts;
                  break;
                case 'friends':
                  filteredPosts = friendsPosts;
                  break;
                case 'following':
                  filteredPosts = followingPosts;
                  break;
                case 'all':
                default:
                  // Merge all sources and deduplicate
                  filteredPosts = [...posts, ...friendsPosts, ...followingPosts]
                    .filter((post, index, self) =>
                      index === self.findIndex(p => p.id === post.id)
                    );
                  break;
              }

              // Apply default popular sorting for popular tab
              let sortedPosts = [...filteredPosts];
              if (activeTab === 'popular' && sortBy === 'newest') {
                // Default to popular sorting for popular tab if no other sort selected
                sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
              } else {
                // Apply sorting based on sortBy (can override popular tab default)
                switch(sortBy) {
                  case 'newest':
                    sortedPosts = sortedPosts.sort((a, b) =>
                      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    );
                    break;
                  case 'oldest':
                    sortedPosts = sortedPosts.sort((a, b) =>
                      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                    );
                    break;
                  case 'popular':
                    sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
                    break;
                  case 'unpopular':
                    sortedPosts = sortedPosts.sort((a, b) =>
                      (b.dislikes || 0) - (a.dislikes || 0)
                    );
                    break;
                  case 'controversial':
                    sortedPosts = sortedPosts.sort((a, b) => {
                      const aControversy = Math.min(a.likes, a.dislikes || 0);
                      const bControversy = Math.min(b.likes, b.dislikes || 0);
                      return bControversy - aControversy;
                    });
                    break;
                  default:
                    break;
                }
              }

              return sortedPosts.length === 0 ? (
              <div className="text-center py-16">
                <h2 className="text-2xl font-bold mb-3" style={{ color: localTheme.textColor }}>No community posts yet</h2>
                <p className="text-base opacity-70" style={{ color: localTheme.textColor }}>Join a community and start sharing!</p>
              </div>
            ) : (
              <div className="w-full" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  maxWidth: isMobile ? '100%' : '100%'
                }}>
                {sortedPosts.map((post, index) => {
                  const postStyle = getPostStyle(index);
                  return (
                  <div
                    key={post.id}
                    ref={(el) => {
                      if (el) postRefs.current.set(post.id, el);
                      else postRefs.current.delete(post.id);
                    }}
                    data-post-id={post.id}
                    className="rounded-lg backdrop-blur-sm border relative"
                    style={{
                      background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                      borderColor: `${localTheme.accentColor}40`,
                      marginBottom: `${scaleH(32, scale.h)}px`,
                      padding: `${scaleH(20, scale.h)}px`,
                      ...postStyle
                    }}>
                    {/* Post Badges */}
                    <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                    {/* Moderate Dropdown */}
                    {currentUser && canModeratePost(post as any, {
                      id: parseInt(currentUser.id),
                      username: currentUser.username,
                      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
                      isModerator: (currentUser as any).isSiteModerator || false
                    }) && (
                    <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                      <button
                        onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)}
                        className="p-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {showModerateMenu === post.id && (
                        <>
                          <div
                            className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                            style={{
                              backgroundColor: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                              borderColor: localTheme.accentColor + '60',
                              border: '1px solid',
                              zIndex: 1001
                            }}
                          >
                            <button
                              onClick={() => handleEditPost(post)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Edit3 size={16} />
                              Edit
                            </button>
                            <button
                              onClick={() => handlePinPost(post)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Pin size={16} />
                              {post.isPinned ? 'Unpin' : 'Pin'}
                            </button>
                            {(currentUser as any).isSuperAdmin && (
                              <button
                                onClick={() => handlePinToFeed(post)}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Pin size={16} />
                                {(post as any).isPinnedToFeed ? 'Unpin from Feed' : 'Pin to Feed'}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeletePost(post)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: '#ef4444',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#ef444420';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Trash size={16} />
                              Delete
                            </button>
                          </div>
                          <div
                            className="fixed inset-0"
                            style={{ zIndex: 90 }}
                            onClick={handleCloseShowModerateMenu}
                          />
                        </>
                      )}
                    </div>
                    )}
                    <div className="flex items-start gap-3 mb-3">
                      <Link to={post.authorUsername ? `/user/${post.authorUsername}` : '#'} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80" style={{
                        background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                      }}>
                        {post.authorAvatar ? (
                          <img src={post.authorAvatar} alt={post.author} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <PixelatedTurtle size={20} color={localTheme.accentColor} />
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-0.5 mb-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold truncate" style={{ color: localTheme.textColor }}>{post.author}</h3>
                            <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>in</span>
                            <Link
                              to={`/community/${post.communitySlug || post.communityName?.toLowerCase().replace(/\s+/g, '-') || post.communityId}`}
                              className="text-xs font-semibold cursor-pointer hover:opacity-80"
                              style={{ color: localTheme.accentColor }}
                            >
                              {post.communityName}
                            </Link>
                            <span className="text-xs flex-shrink-0" style={{ color: localTheme.textColor + '99' }}>{post.timestamp}</span>
                          </div>
                          {post.authorUsername && (
                            <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>@{post.authorUsername}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Moderation Disclaimer */}
                    {post.editedByModeratorId && (
                      <div className="mb-3 px-3 py-2 rounded border" style={{
                        backgroundColor: '#854d0e20',
                        borderColor: '#ca8a0480'
                      }}>
                        <div className="flex items-center gap-2">
                          <AlertCircle size={14} className="text-yellow-400" />
                          <p className="text-xs text-yellow-100">
                            This post was edited by a moderator
                            {post.editedByModeratorAt && ` on ${formatDate(post.editedByModeratorAt)}`}
                          </p>
                        </div>
                        {post.moderatorEditReason && (
                          <p className="text-xs text-yellow-100/80 mt-1">
                            Reason: {post.moderatorEditReason}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Media above content */}
                    {post.aboveContent && post.mediaUrl && (
                      <div className="mb-3 rounded-lg overflow-hidden">
                        <MediaDisplay
                          fileId={post.mediaUrl}
                          variant="medium"
                          alt="Post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px' }}
                          isVideo={post.mediaUrl.includes('video') || post.mediaUrl.includes('.mp4')}
                        />
                      </div>
                    )}

                    {/* Post Title and Content */}
                    <Link
                      to={post.slug && post.communitySlug
                        ? (post.postType === 'forum'
                          ? getForumPostUrl(post.communitySlug, post.slug)
                          : getCommunityPostUrl(post.communitySlug, post.slug))
                        : `/posts/${post.id}`
                      }
                      className="block hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      {post.title && (
                        <h2 className="text-base font-bold mb-2" style={{ color: localTheme.textColor }}>
                          {post.title}
                        </h2>
                      )}
                      <p className="text-sm leading-relaxed mb-3 whitespace-pre-wrap" style={{ color: localTheme.textColor }}>
                        {post.content}
                      </p>
                    </Link>

                    {/* Media below content */}
                    {post.belowContent && post.mediaUrl && (
                      <div className="mt-3 rounded-lg overflow-hidden">
                        <MediaDisplay
                          fileId={post.mediaUrl}
                          variant="medium"
                          alt="Post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px' }}
                          isVideo={post.mediaUrl.includes('video') || post.mediaUrl.includes('.mp4')}
                        />
                      </div>
                    )}

                    {/* Post Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity"
                            style={{
                              backgroundColor: localTheme.accentColor + '30',
                              color: localTheme.textColor
                            }}
                          >
                            <Hash size={10} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center justify-between pt-3 " style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={(e) => likePost(post.id, e)}
                          className="flex items-center gap-1.5 transition-colors"
                          style={{ color: post.liked ? '#ef4444' : localTheme.textColor + '99' }}
                        >
                          <Heart size={16} fill={post.liked ? '#ef4444' : 'none'} />
                          <span className="text-xs">{post.likes}</span>
                        </button>

                        <button
                          onClick={(e) => dislikePost(post.id, e)}
                          className="flex items-center gap-1.5 transition-colors"
                          style={{ color: post.disliked ? '#f97316' : localTheme.textColor + '99' }}
                        >
                          <ThumbsDown size={16} fill={post.disliked ? '#f97316' : 'none'} />
                          <span className="text-xs">{post.dislikes}</span>
                        </button>

                        <button
                          onClick={() => navigate(
                            post.slug && post.communitySlug
                              ? (post.postType === 'forum'
                                  ? getForumPostUrl(post.communitySlug, post.slug)
                                  : getCommunityPostUrl(post.communitySlug, post.slug))
                              : `/posts/${post.id}`
                          )}
                          className="flex items-center gap-1.5 transition-colors hover:text-purple-400"
                          style={{ color: localTheme.textColor + '99' }}
                        >
                          <MessageSquare size={16} />
                          <span className="text-xs">{post.comments}</span>
                        </button>

                        <button
                          onClick={() => handleRepost(post.id)}
                          className="flex items-center gap-1.5 transition-colors hover:text-green-400"
                          style={{ color: localTheme.textColor + '99' }}
                        >
                          <Repeat2 size={16} />
                          <span className="text-xs">{post.shares}</span>
                        </button>

                        <button
                          onClick={() => handleShare(post.id)}
                          className="flex items-center gap-1.5 transition-colors hover:text-blue-400"
                          style={{ color: localTheme.textColor + '99' }}
                        >
                          <Share2 size={16} />
                        </button>
                      </div>

                      <button className="transition-colors" style={{ color: localTheme.textColor + '99' }}>
                        <Bookmark size={16} />
                      </button>
                    </div>

                    {/* Comment Preview - Max 3 comments */}
                    {post.replies && post.replies.length > 0 && (() => {
                      const previewComments = selectPreviewComments(post.replies);
                      return (
                        <div className="mt-4 pt-4 " style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                          <div className="space-y-3">
                            {previewComments.map((reply) => (
                              <div key={reply.id} className="flex gap-3">
                                <Link
                                  to={`/user/${reply.author.username}`}
                                  className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-opacity hover:opacity-80"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  {reply.author.profilePicture ? (
                                    <img src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture} alt={reply.author.profileName || reply.author.username} className="w-full h-full rounded-full object-cover" />
                                  ) : (
                                    <PixelatedTurtle size={16} color={localTheme.accentColor} />
                                  )}
                                </Link>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-semibold" style={{ color: localTheme.textColor }}>
                                      {reply.author.profileName || reply.author.username}
                                    </span>
                                    <span className="text-xs" style={{ color: localTheme.textColor + '66' }}>
                                      {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  </div>
                                  <p className="text-xs" style={{ color: localTheme.textColor + 'cc' }}>{reply.content}</p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <button
                                      onClick={(e) => likeComment(reply.id, e)}
                                      className="flex items-center gap-1 transition-opacity hover:opacity-70"
                                      style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '66' }}
                                    >
                                      <Heart size={12} className={reply.liked ? "fill-current" : ""} />
                                      <span className="text-xs">{reply.likes}</span>
                                    </button>
                                    <button
                                      onClick={(e) => dislikeComment(reply.id, e)}
                                      className="flex items-center gap-1 transition-opacity hover:opacity-70"
                                      style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '66' }}
                                    >
                                      <ThumbsDown size={12} className={reply.disliked ? "fill-current" : ""} />
                                      <span className="text-xs">{reply.dislikes}</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {post.comments > 3 && (
                            <div className="mt-2">
                              <button
                                className="text-xs transition-opacity hover:opacity-70"
                                style={{ color: localTheme.accentColor }}
                                onClick={() => {
                                  const url = post.postType === 'forum'
                                    ? getForumPostUrl(post.communitySlug || '', post.slug || '')
                                    : getCommunityPostUrl(post.communitySlug || '', post.slug || '');
                                  navigate(url);
                                }}
                              >
                                View all {post.comments} comments
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  );
                })}
                </div>
              </div>
            );
            })()}
          </div>

        {/* RIGHT SECTION - Sidebar Widgets */}
        <div
          className="absolute pt-6 transition-all duration-300"
          style={{
            right: '2rem',
            top: 0,
            opacity: isMobile ? (currentSection === 'right' ? 1 : 0) : 1,
            pointerEvents: isMobile ? (currentSection === 'right' ? 'auto' : 'none') : 'auto'
          }}>
          <div className="pb-4"></div>
          <div className="w-full py-5 mb-6"></div>
          <div className="w-full mt-8 mb-12" style={{ height: '20px' }}></div>
          <div className="flex gap-6">
          {/* Navigate Widget */}
          <div style={{ width: `${scaleW(320, scale.w)}px` }}>
            <div className="rounded-lg backdrop-blur-sm border sticky" style={{
              top: `${scaleH(85, scale.h)}px`,
              background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
              borderColor: `${localTheme.accentColor}40`,
              zIndex: 10,
              padding: 0
            }}>
                <h3 className="font-bold text-center border-b" style={{
                  color: localTheme.textColor,
                  borderColor: `${localTheme.accentColor}40`,
                  fontSize: `${scaleW(18, scale.w)}px`,
                  paddingTop: `${scaleH(12, scale.h)}px`,
                  paddingBottom: `${scaleH(12, scale.h)}px`
                }}>Navigate</h3>

                <div style={{ padding: `${scaleH(12, scale.h)}px` }}>
                  {/* Navigate Input */}
                  <input
                    type="text"
                    placeholder="Navigate..."
                    value={sidebarSearchQuery}
                    onChange={(e) => setSidebarSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && sidebarSearchQuery.trim()) {
                        setShowSidebarSearchResults(true);
                      }
                    }}
                    onFocus={() => {
                      if (sidebarSearchQuery.trim()) {
                        setShowSidebarSearchResults(true);
                      }
                    }}
                    className="w-full rounded-lg outline-none transition-all"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: localTheme.textColor,
                      borderColor: `${localTheme.accentColor}40`,
                      border: '1px solid',
                      padding: `${scaleH(12, scale.h)}px ${scaleW(12, scale.w)}px`,
                      height: `${scaleH(40, scale.h)}px`,
                      fontSize: `${scaleW(14, scale.w)}px`,
                      marginBottom: `${scaleH(12, scale.h)}px`
                    }}
                  />

                  {/* Search Type Selector */}
                  <div className="flex gap-0 rounded-lg overflow-hidden" style={{
                    border: `1px solid ${localTheme.accentColor}40`
                  }}>
                  <button
                    onClick={() => setSidebarSearchType('people')}
                    className="flex-1 font-semibold transition-all"
                    style={{
                      backgroundColor: sidebarSearchType === 'people' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.05)',
                      color: localTheme.textColor,
                      borderRight: `1px solid ${localTheme.accentColor}40`,
                      paddingTop: `${scaleH(8, scale.h)}px`,
                      paddingBottom: `${scaleH(8, scale.h)}px`,
                      fontSize: `${scaleW(14, scale.w)}px`
                    }}
                  >
                    People
                  </button>
                  <button
                    onClick={() => setSidebarSearchType('communities')}
                    className="flex-1 font-semibold transition-all"
                    style={{
                      backgroundColor: sidebarSearchType === 'communities' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.05)',
                      color: localTheme.textColor,
                      borderRight: `1px solid ${localTheme.accentColor}40`,
                      paddingTop: `${scaleH(8, scale.h)}px`,
                      paddingBottom: `${scaleH(8, scale.h)}px`,
                      fontSize: `${scaleW(14, scale.w)}px`
                    }}
                  >
                    Communities
                  </button>
                  <button
                    onClick={() => setSidebarSearchType('tags')}
                    className="flex-1 font-semibold transition-all"
                    style={{
                      backgroundColor: sidebarSearchType === 'tags' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.05)',
                      color: localTheme.textColor,
                      paddingTop: `${scaleH(8, scale.h)}px`,
                      paddingBottom: `${scaleH(8, scale.h)}px`,
                      fontSize: `${scaleW(14, scale.w)}px`
                    }}
                  >
                    Tags
                  </button>
                  </div>

                  {/* Animated Search Results Dropdown */}
                  {showSidebarSearchResults && sidebarSearchQuery.trim() && (
                    <div
                      className="mt-3 rounded-lg border overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                      borderColor: `${localTheme.accentColor}40`,
                      maxHeight: '400px',
                      animation: 'slideDown 0.3s ease-out'
                    }}
                  >
                    <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                      {/* People Results */}
                      {sidebarSearchType === 'people' && (
                        <div className="p-3 space-y-2">
                          {isSearching ? (
                            <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                              Searching...
                            </div>
                          ) : sidebarSearchPeople.length > 0 ? (
                            sidebarSearchPeople.map((person) => (
                              <div
                                key={person.id}
                                onClick={() => {
                                  navigate(`/user/${person.username}`);
                                  setShowSidebarSearchResults(false);
                                  setSidebarSearchQuery('');
                                }}
                                className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                              >
                                {person.avatar ? (
                                  <img
                                    src={person.avatar}
                                    alt={person.displayName}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{
                                      background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                    }}
                                  >
                                    <PixelatedTurtle size={20} color={localTheme.accentColor} />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>{person.displayName}</div>
                                  <div className="text-xs truncate" style={{ color: localTheme.textColor + '99' }}>@{person.username}</div>
                                </div>
                                <div className="text-xs whitespace-nowrap" style={{ color: localTheme.textColor + '66' }}>
                                  {person.followers} followers
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                              No people found
                            </div>
                          )}
                        </div>
                      )}

                      {/* Communities Results */}
                      {sidebarSearchType === 'communities' && (
                        <div className="p-3 space-y-2">
                          {isSearching ? (
                            <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                              Searching...
                            </div>
                          ) : sidebarSearchCommunities.length > 0 ? (
                            sidebarSearchCommunities.map((comm) => (
                              <div
                                key={comm.id}
                                onClick={() => {
                                  navigate(`/community/${comm.nameSlug || comm.name.toLowerCase().replace(/\s+/g, '-')}`);
                                  setShowSidebarSearchResults(false);
                                  setSidebarSearchQuery('');
                                }}
                                className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                              >
                                {comm.avatar ? (
                                  <img
                                    src={comm.avatar}
                                    alt={comm.name}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{
                                      background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                    }}
                                  >
                                    <PixelatedCoral size={20} />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>{comm.name}</div>
                                  <div className="text-xs truncate" style={{ color: localTheme.textColor + '99' }}>{comm.members} members</div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                              No communities found
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tags Results */}
                      {sidebarSearchType === 'tags' && (
                        <div className="p-3 space-y-2">
                          {isSearching ? (
                            <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                              Searching...
                            </div>
                          ) : sidebarSearchTags.length > 0 ? (
                            sidebarSearchTags.map((tagData) => (
                              <div
                                key={tagData.tag}
                                className="flex items-center gap-3 p-2 rounded-lg transition-all"
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                              >
                                <div
                                  onClick={() => {
                                    navigate(`/tags/${tagData.tag}`);
                                    setShowSidebarSearchResults(false);
                                    setSidebarSearchQuery('');
                                  }}
                                  className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80"
                                >
                                  <div
                                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{
                                      backgroundColor: localTheme.accentColor + '30',
                                      border: `1px solid ${localTheme.accentColor}`
                                    }}
                                  >
                                    <Hash size={20} style={{ color: localTheme.textColor }} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>#{tagData.tag}</div>
                                    <div className="text-xs truncate" style={{ color: localTheme.textColor + '99' }}>{tagData.count} posts</div>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => handleToggleFollowTag(tagData.tag, e)}
                                  className="px-3 py-1 rounded text-xs font-semibold transition-all hover:opacity-80 flex-shrink-0"
                                  style={{
                                    backgroundColor: followedTags.includes(tagData.tag) ? 'rgba(255, 255, 255, 0.1)' : localTheme.accentColor,
                                    color: localTheme.textColor,
                                    border: followedTags.includes(tagData.tag) ? `1px solid ${localTheme.accentColor}` : 'none'
                                  }}
                                >
                                  {followedTags.includes(tagData.tag) ? 'Following' : 'Follow'}
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                              No tags found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>
            </div>

            {/* Discover Widget */}
            <div className="rounded-lg backdrop-blur-sm border" style={{
              background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
              borderColor: `${localTheme.accentColor}40`,
              padding: 0,
              marginTop: `${scaleH(24, scale.h)}px`,
              maxHeight: '80vw',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 10
            }}>
              <div className="flex items-center justify-between " style={{ borderBottom: `1px solid ${localTheme.accentColor}40`, paddingLeft: `${scaleW(20, scale.w)}px`, paddingRight: `${scaleW(20, scale.w)}px`, paddingTop: `${scaleH(12, scale.h)}px`, paddingBottom: `${scaleH(12, scale.h)}px`, flexShrink: 0 }}>
                <h3 className="font-bold flex items-center" style={{ color: localTheme.textColor, fontSize: `${scaleW(18, scale.w)}px`, gap: `${scaleW(8, scale.w)}px` }}>
                  <Compass size={scaleW(20, scale.w)} />
                  Discover
                </h3>
                <button
                  onClick={refreshDiscoverCommunities}
                  className="rounded-lg transition-all hover:opacity-70"
                  style={{
                    backgroundColor: localTheme.accentColor + '30',
                    color: localTheme.textColor,
                    padding: `${scaleH(8, scale.h)}px`
                  }}
                  title="Refresh communities"
                >
                  <svg width={scaleW(16, scale.w)} height={scaleH(16, scale.h)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                </button>
              </div>
              <div style={{ padding: `${scaleH(20, scale.h)}px ${scaleW(20, scale.w)}px`, overflow: 'auto', flex: 1 }}>
                {discoverCommunities.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center">
                    <Compass size={scaleW(48, scale.w)} style={{ color: localTheme.textColor + '40', marginBottom: `${scaleH(12, scale.h)}px` }} />
                    <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(14, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                      No communities to discover
                    </p>
                    <p style={{ color: localTheme.textColor + '66', fontSize: `${scaleW(12, scale.w)}px` }}>
                      You've joined all available communities!
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(12, scale.h)}px` }}>
                    {discoverCommunities.map((community) => (
                      <Link
                        key={community.id}
                        to={`/community/${community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="group cursor-pointer rounded-lg transition-all hover:bg-white/5 block"
                        style={{ padding: `${scaleH(8, scale.h)}px 0` }}
                      >
                        <div className="flex items-center" style={{ minWidth: 'max-content', gap: `${scaleW(12, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                          <div className="rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0" style={{
                            backgroundImage: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`,
                            width: `${scaleW(40, scale.w)}px`,
                            height: `${scaleW(40, scale.w)}px`
                          }}>
                            {community.avatar ? (
                              <img src={community.avatar} alt={community.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <Users size={scaleW(20, scale.w)} style={{ color: localTheme.textColor }} />
                            )}
                          </div>
                          <div className="flex-1" style={{ minWidth: 'max-content' }}>
                            <div className="font-semibold group-hover:opacity-80 transition-opacity" style={{ color: localTheme.textColor, whiteSpace: 'nowrap', fontSize: `${scaleW(14, scale.w)}px` }}>
                              {community.name}
                            </div>
                            <div style={{ color: localTheme.textColor + '99', whiteSpace: 'nowrap', fontSize: `${scaleW(12, scale.w)}px` }}>
                              {community.memberCount} members
                            </div>
                          </div>
                        </div>
                        {community.description && (
                          <p className="line-clamp-2" style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(12, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                            {community.description}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Joined Communities */}
          <div style={{ width: `${scaleW(320, scale.w)}px` }}>
            <div className="rounded-lg backdrop-blur-sm border sticky" style={{
                top: `${scaleH(85, scale.h)}px`,
                background: getCardBackground(ensureThemeColors, customization, theme.cardBg),
                borderColor: `${localTheme.accentColor}40`,
                marginBottom: `${scaleH(48, scale.h)}px`,
                marginTop: 0,
                padding: 0,
                maxHeight: '120vw',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 10
              }}>
                <h3 className="font-bold text-center" style={{ color: localTheme.textColor, borderBottom: `${localTheme.accentColor}40`, paddingLeft: `${scaleW(20, scale.w)}px`, paddingRight: `${scaleW(20, scale.w)}px`, paddingTop: `${scaleH(12, scale.h)}px`, paddingBottom: `${scaleH(12, scale.h)}px`, fontSize: `${scaleW(18, scale.w)}px`, flexShrink: 0 }}>Joined</h3>
                <div style={{ padding: `${scaleH(20, scale.h)}px ${scaleW(20, scale.w)}px`, overflow: 'auto', flex: 1 }}>
                  {joinedCommunities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center">
                      <Users size={scaleW(48, scale.w)} style={{ color: localTheme.textColor + '40', marginBottom: `${scaleH(12, scale.h)}px` }} />
                      <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(14, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                        No joined communities yet
                      </p>
                      <p style={{ color: localTheme.textColor + '66', fontSize: `${scaleW(12, scale.w)}px` }}>
                        Explore and join communities
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(12, scale.h)}px` }}>
                      {joinedCommunities.map((community) => (
                        <div
                          key={community.id}
                          className="group cursor-pointer rounded-lg transition-all hover:bg-white/5"
                          onClick={() => navigate(`/community/${community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-')}`)}
                          style={{ padding: `${scaleH(8, scale.h)}px 0` }}
                        >
                          <div className="flex items-center" style={{ minWidth: 'max-content', gap: `${scaleW(12, scale.w)}px` }}>
                            <div className="rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0" style={{
                              backgroundImage: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`,
                              width: `${scaleW(40, scale.w)}px`,
                              height: `${scaleW(40, scale.w)}px`
                            }}>
                              {community.avatar ? (
                                <img src={community.avatar} alt={community.name} className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <Users size={scaleW(20, scale.w)} style={{ color: localTheme.textColor }} />
                              )}
                            </div>
                            <div className="flex-1" style={{ minWidth: 'max-content' }}>
                              <div className="font-semibold group-hover:opacity-80 transition-opacity" style={{ color: localTheme.textColor, whiteSpace: 'nowrap', fontSize: `${scaleW(14, scale.w)}px` }}>
                                {community.name}
                              </div>
                              <div style={{ color: localTheme.textColor + '99', whiteSpace: 'nowrap', fontSize: `${scaleW(12, scale.w)}px` }}>
                                {community.memberCount} members
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Post Creation Modal */}
      <CommunityPostModal
        isOpen={showPostWidget}
        onClose={() => setShowPostWidget(false)}
        onSubmit={handleCreatePost}
        currentUser={currentUser}
        theme={localTheme}
        scale={scale}
        scaleW={scaleW}
        scaleH={scaleH}
        getCardBackground={getCardBackground}
        customization={customization}
        communities={joinedCommunities}
      />

      {/* Refresh Button */}
      <button
        onClick={handleRefresh}
        className="group fixed z-40 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:shadow-xl"
        style={{
          top: `${scaleH(100, scale.h)}px`,
          right: `${scaleW(112, scale.w)}px`,
          backgroundColor: localTheme.accentColor,
          color: localTheme.textColor,
          width: `${scaleW(56, scale.w)}px`,
          height: `${scaleH(56, scale.h)}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="Refresh"
        aria-label="Refresh"
      >
        <RefreshCw
          size={Math.round(scaleW(24, scale.w))}
          className={`transition-transform ${isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'}`}
        />

        {/* Tooltip */}
        <span
          className="absolute right-full mr-3 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none"
          style={{
            backgroundColor: localTheme.accentColor,
            color: localTheme.textColor,
          }}
        >
          Refresh
        </span>
      </button>

      {/* Standardized Theme Customizer Button */}
      <ThemeCustomizerButton
        onClick={handleToggleShowSiteCustomizer}
        tooltipText="Customize Site Theme"
        accentColor={localTheme.accentColor}
        textColor={localTheme.textColor}
        show={true}
      />

      {/* Site Theme Customizer Modal */}
      {currentUser && (
        <SiteThemeCustomizer
          isOpen={showSiteCustomizer}
          onClose={() => setShowSiteCustomizer(false)}
          userId={currentUser.id}
          initialCustomization={customization}
          initialAnimations={themeAnimations}
          initialAnimationsEnabled={animationsEnabled}
        />
      )}

      {/* Repost Modal */}
      {showRepostModal && (
        <div
          ref={repostModalRef}
          className="fixed z-[100] max-w-md w-full mx-4"
          style={{
            left: '50%',
            top: '5rem',
            transform: `translate(calc(-50% + ${repostModalPosition.x}px), ${repostModalPosition.y}px)`,
            cursor: isDraggingRepost ? 'grabbing' : 'default'
          }}
          onMouseMove={(e) => {
            if (isDraggingRepost) {
              const deltaX = e.clientX - dragStartRepost.x;
              const deltaY = e.clientY - dragStartRepost.y;
              setRepostModalPosition({ x: repostModalPosition.x + deltaX, y: repostModalPosition.y + deltaY });
              setDragStartRepost({ x: e.clientX, y: e.clientY });
            }
          }}
          onMouseUp={() => setIsDraggingRepost(false)}
          onMouseLeave={() => setIsDraggingRepost(false)}
        >
          <div className="rounded-lg backdrop-blur-sm border shadow-2xl" style={{
            background: getCardBackground(ensureThemeColors, customization, theme.cardBg).replace(/[\d.]+\)$/, '0.95)'),
            borderColor: `${localTheme.accentColor}40`
          }}>
            <div
              className="flex items-center justify-between mb-4 p-6 pb-2 cursor-grab active:cursor-grabbing"
              onMouseDown={(e) => {
                setIsDraggingRepost(true);
                setDragStartRepost({ x: e.clientX, y: e.clientY });
              }}
              style={{
                borderBottom: `1px solid ${localTheme.accentColor}20`,
                userSelect: 'none'
              }}
            >
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Repost</h3>
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostModalPosition({ x: 0, y: 0 });
                }}
                className="transition-colors hover:opacity-70"
                style={{ color: localTheme.textColor }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X size={24} />
              </button>
            </div>
            <div className="px-6 pb-6">
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Add an optional comment to your repost:</p>
            <textarea
              value={repostComment}
              onChange={(e) => setRepostComment(e.target.value)}
              placeholder="What do you think about this?"
              className="w-full p-3 rounded-lg outline-none resize-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
              rows={4}
              maxLength={280}
            />
            <div className="text-right text-sm mb-4" style={{ color: localTheme.textColor + '99' }}>{repostComment.length}/280</div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostComment('');
                }}
                className="flex-1 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRepost}
                disabled={isReposting}
                className="flex-1 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                {isReposting ? 'Reposting...' : 'Repost'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] max-w-md w-full mx-4">
          <div className="rounded-lg p-6 backdrop-blur-sm border shadow-2xl" style={{
            background: getCardBackground(ensureThemeColors, customization, theme.cardBg).replace(/[\d.]+\)$/, '0.95)'),
            borderColor: `${localTheme.accentColor}40`
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Share Post</h3>
              <button onClick={handleCloseShowShareModal} className="transition-colors hover:opacity-70" style={{ color: localTheme.textColor }}>
                <X size={24} />
              </button>
            </div>
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Share this post:</p>
            <button
              onClick={handleCopyLink}
              className="w-full py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
            >
              <Link2 size={18} />
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Create Community Modal */}
      {showCreateCommunityModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div
            className="rounded-xl shadow-2xl border p-8 w-[500px]"
            style={{
              backgroundColor: localTheme.cardBg?.replace(/[\d.]+\)$/, '0.95)') || 'rgba(255, 255, 255, 0.95)',
              borderColor: localTheme.accentColor + '60'
            }}
          >
            <h2 className="text-2xl font-bold mb-6" style={{ color: localTheme.textColor }}>
              Create Community
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: localTheme.textColor }}>
                  Community Name:
                </label>
                <input
                  type="text"
                  value={newCommunityName}
                  onChange={(e) => setNewCommunityName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border outline-none"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: `${localTheme.accentColor}40`,
                    color: localTheme.textColor
                  }}
                  placeholder="Enter community name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: localTheme.textColor }}>
                  Community Title:
                </label>
                <input
                  type="text"
                  value={newCommunityTitle}
                  onChange={(e) => setNewCommunityTitle(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border outline-none"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: `${localTheme.accentColor}40`,
                    color: localTheme.textColor
                  }}
                  placeholder="Enter community title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: localTheme.textColor }}>
                  Community Bio:
                </label>
                <textarea
                  value={newCommunityBio}
                  onChange={(e) => setNewCommunityBio(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 rounded-lg border outline-none resize-none"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: `${localTheme.accentColor}40`,
                    color: localTheme.textColor
                  }}
                  placeholder="Enter community bio"
                />
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newCommunityIsPublic}
                    onChange={(e) => setNewCommunityIsPublic(e.target.checked)}
                    className="w-5 h-5 rounded cursor-pointer"
                    style={{ accentColor: localTheme.accentColor }}
                  />
                  <span className="text-sm font-medium" style={{ color: localTheme.textColor }}>
                    Public Community (anyone can view and join)
                  </span>
                </label>
              </div>

              <div className="flex gap-3 items-center">
                <button
                  onClick={() => {
                    setNewCommunityName('');
                    setNewCommunityTitle('');
                    setNewCommunityBio('');
                    setNewCommunityIsPublic(true);
                    setShowCreateCommunityModal(false);
                  }}
                  className="px-6 py-3 rounded-lg font-semibold transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: localTheme.textColor,
                    marginRight: 'auto',
                    marginLeft: '0.5rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!newCommunityName.trim() || !newCommunityTitle.trim()) {
                      alert('Please fill in community name and title');
                      return;
                    }

                    setIsCreatingCommunity(true);
                    try {
                      const { createCommunity } = await import('../../lib/api-client');
                      const community = await createCommunity({
                        name: newCommunityName,
                        description: newCommunityBio || undefined,
                        isPublic: newCommunityIsPublic
                      });


                      // Reset form and close modal
                      setNewCommunityName('');
                      setNewCommunityTitle('');
                      setNewCommunityBio('');
                      setNewCommunityIsPublic(true);
                      setShowCreateCommunityModal(false);

                      // Redirect to the new community page
                      const slug = community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-');
                      navigate(`/community/${slug}`);
                    } catch (error) {
                      alert(error instanceof Error ? error.message : 'Failed to create community');
                    } finally {
                      setIsCreatingCommunity(false);
                    }
                  }}
                  disabled={isCreatingCommunity}
                  className="flex-1 py-3 rounded-lg font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor
                  }}
                >
                  {isCreatingCommunity ? 'Creating...' : 'Create Community'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}
